import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useCallback } from 'react';
import { callAPI } from '../helper/apiUtils';
import UserAvatar from './UserAvatar';
import EmailSenderAvatar from './EmailSenderAvatar';
import MaterialIcon from './MaterialIcon';
import '../styles/components.css';
import './SearchBar.css';
import { API_ROOT, BASE_URL, makeid, TOGGLE_DRAWER, buildImageUrl } from '../helper/constants';
import { SOCKET_BASE } from '../helper/SocketObj';
import { useDispatch, useSelector } from 'react-redux';
import { SocketContext } from '../context/socket';
import { $user } from '../helper/UserFactory';
import { encryptMessage } from '../utils/encryptDecryptFunction';
import { getVoiceMediaStream } from '../utils/voiceRecordingConstraints';
import { $crud } from '../helper/CrudFactory';
import LanguageContext from '../helper/LanguageContext';
import { EN } from '../Languages/EN';
import { DE } from '../Languages/DE';
import { resolveStripeFlags } from '../utils/stripeFeature';
import { canShowEmailCalendarIntegration } from '../utils/kjuupIoEntitlement';
import { looksLikeHtml } from '../helper/emailBodyFormat';
import {
  RESET_LATEST_MESSAGES, SET_SEARCH_STATUS, SET_PENDING_EMAIL,
  OPEN_CHATS,
  OPEN_PROJECTS,
  OPEN_BOTTOM_PROJECTS,
  OPEN_BOTTOM_CHATS,
  SET_JOIN_REQUEST_EXTERNAL,
  SET_JOIN_REQUEST_INTERNAL,
  SET_COMPANY_IMAGE,
  SET_COMPANY_ID,
  TOGGLE_PROJECT,
  RIGHT_TOP,
  SET_OPEN_SIDE_BAR,
  TOGGLE_CHAT,
  UPDATE_PROJECT_ACTION_FIRE,
  SET_IN_ACTIVITY,
  TOGGLE_LATEST_MSG,
  TOGGLE_WORK_AREA,
  SET_DEEPLINK_DATA,
  SET_DEEPLINK_LOADING,
  FROM_FROROIEGN_PROFILE,
  GROUPS,
  GROUP_CHATS,
  SET_CHAT_FULLSCREEN,
  SET_ACTIVITY_MESSAGES,
  SET_ACTIVITY_MESSAGES_LAST_FETCHED,
  TOGGLE_TODO_VIEW,
} from '../store';
import { useHistory, useLocation, useParams } from "react-router";
import { onlyUsername } from '../pages/Project/GroupChat/getUsername';
import { stripMarkdown } from "../utils/conversationUtils";
import { isTeamsChat, isTeamsTarget, knownTeamsChat } from "../utils/teams/teamsChats";
import { openChatMessage, openProjectTask } from "../pages/Cockpit/cockpitNavigation";
import { DOC_ACCEPT_ATTRIBUTE, DOC_EXTENSIONS } from "../utils/documentAlbum";
import { parseSearchIntent, contentSearchTokens } from '../helper/parseSearchIntent';
import {
  composerMarkdownToHtml as sharedComposerMarkdownToHtml,
  htmlToMarkdown as sharedHtmlToMarkdown,
  applyActiveFormatsIfChanged,
  syncComposerEmptiness,
  pasteAsPlainText,
  handleComposerDrop,
  handleComposerEnterKey,
} from "../utils/composerUtils";



// Survives SearchBar remounts between result → email → Back.
let globalPendingSearchScroll = null; // { top: number } | null

const SearchBar = () => {
  const { isGerm, strings } = useContext(LanguageContext);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [groupResults, setGroupResults] = useState([]); // Groups search results
  const [roomResults, setRoomResults] = useState([]); // Rooms search results (roomName from rooms array)
  const [userResults, setUserResults] = useState([]); // Users search results
  // Merged email hits with _matchContent / _matchAddress / _matchAttachment flags.
  const [emailResults, setEmailResults] = useState([]);
  const [emailFilterChip, setEmailFilterChip] = useState('all'); // all | content | addresses | attachments | hasAttachments | unread
  const [emailParticipant, setEmailParticipant] = useState('');
  const [documentResults, setDocumentResults] = useState([]); // Document search results
  const [todoResults, setTodoResults] = useState([]); // ToDo search results
  const [isEmailsLoading, setIsEmailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('messages'); // messages | emails | groups | rooms | users | documents | todos
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false); // Loading state for groups
  const [isRoomsLoading, setIsRoomsLoading] = useState(false); // Loading state for rooms
  const [isUsersLoading, setIsUsersLoading] = useState(false); // Loading state for users
  const [isTyping, setIsTyping] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showResults, setShowResults] = useState(false);
  // Hide popup without unmounting when opening a result — preserves scroll for Back.
  const [searchOverlaySuppressed, setSearchOverlaySuppressed] = useState(false);
  const keepSearchMountedForBackRef = useRef(false);
  const searchMountPathRef = useRef('');
  const [replyingTo, setReplyingTo] = useState(null); // message id
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyExiting, setReplyExiting] = useState(null); // for exit animation
  const [groupSecret, setGroupSecret] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]); // For multiple images
  const [attachmentError, setAttachmentError] = useState(null);
  const searchTimeout = useRef(null);
  const resultsRef = useRef(null);
  const resultsListRef = useRef(null); // scrollable .search-results panel
  const popupInputRef = useRef(null);
  const user = useSelector(state => state.user);
  const profile = useSelector((state) => state.profile);
  const subscriptionState = useSelector((state) => state.subscription) || {};
  const {
    stripeFeatureEnabled,
    companyStripeFeatureEnabled,
    isKjuupPlusMember,
    stripeUiEnabled,
  } = resolveStripeFlags(profile, subscriptionState);
  const hasActiveKjuupIoPlan = !stripeUiEnabled || !!subscriptionState.active;
  const showEmailSearchHint = canShowEmailCalendarIntegration({
    isKjuupPlusMember,
    hasActiveKjuupIoPlan,
    showCompanyTrialEndedNote: !!subscriptionState.showCompanyTrialEndedNote,
    stripeFeatureEnabled,
    companyStripeFeatureEnabled,
  });
  const isAutoTranslation = useSelector(state => state.isAutoTranslation);
  const isFirstLoad = useRef(true);
  const searchIdRef = useRef(0); // for latest search tracking
  const [isTabsHidden, setIsTabsHidden] = useState(false);
  const lastScrollTop = useRef(0);
  // Preserve scroll when navigating to a result and Back into the popup.
  // Single pending snapshot (not keyed) so restore cannot miss due to tab/chip races.
  const pendingScrollRestoreRef = useRef(null); // { top: number } | null
  const shouldRestoreScrollRef = useRef(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const MAX_MESSAGE_LENGTH = 800;
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);
  const attachmentErrorTimeout = useRef(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const audioRef = useRef(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [activeFormats, setActiveFormats] = useState({});
  const activeFormatsRef = useRef(null);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionInput, setMentionInput] = useState("");
  const richEditorRef = useRef(null);
  const mentionCursorRef = useRef(null);
  const mediaStreamRef = useRef(null); // Add this for the stream
  const deeplinkData = useSelector(state => state.deeplinkData);
  const activityMessages = useSelector(state => state.activityMessages);
  // Add state and ref for audio preview duration
  const [audioPreviewDuration, setAudioPreviewDuration] = useState(null);
  const audioPreviewRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const skippingHistoryPushRef = useRef(false);
  const ignoreOutsideClickRef = useRef(false);
  const showResultsRef = useRef(false);
  const lastPushedSearchKeyRef = useRef('');
  const lastFetchedQueryRef = useRef('');
  const searchMessagesRef = useRef(null);
  const GS_PARAM = 'gs';
  const GQ_PARAM = 'gq';
  const GTAB_PARAM = 'gtab';
  const GADDR_PARAM = 'gaddr';
  const searchIntent = parseSearchIntent(searchValue, emailParticipant);
  const emailComboOnly = searchIntent.intent === 'email' && searchIntent.active;
  const chatComboOnly = searchIntent.intent === 'chat' && searchIntent.active;
  const userLookupOnly = searchIntent.intent === 'user';
  const highlightQuery = [searchIntent.person, ...contentSearchTokens(searchIntent.content || searchIntent.q || '')]
    .filter(Boolean)
    .join(' ') || searchValue;

  useEffect(() => {
    showResultsRef.current = showResults;
  }, [showResults]);

  // Helper to format seconds as mm:ss
  function formatAudioDuration(seconds) {
    if (!isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  // Effect to update audio duration when recordedAudio changes
  useEffect(() => {
    if (recordedAudio && audioPreviewRef.current) {
      const audioEl = audioPreviewRef.current;
      const updateDuration = () => {
        setAudioPreviewDuration(audioEl.duration);
      };
      audioEl.addEventListener('loadedmetadata', updateDuration);
      // If metadata is already loaded
      if (audioEl.readyState >= 1) {
        setAudioPreviewDuration(audioEl.duration);
      }
      return () => {
        audioEl.removeEventListener('loadedmetadata', updateDuration);
      };
    } else {
      setAudioPreviewDuration(null);
    }
  }, [recordedAudio]);

  // In the useEffect for audioPreviewRef, force metadata load
  useEffect(() => {
    if (recordedAudio && audioPreviewRef.current) {
      const audioEl = audioPreviewRef.current;
      // Force reload metadata
      audioEl.load();
    }
  }, [recordedAudio]);

  useEffect(() => {
    dispatch({ type: SET_SEARCH_STATUS, payload: showResults });
  }, [showResults, dispatch]);

  // Helper function to close reply section with smooth animation
  const closeReplySection = useCallback(() => {
    if (replyingTo !== null) {
      setReplyExiting(replyingTo);
      setTimeout(() => {
        setReplyingTo(null);
        setReplyText('');
        if (richEditorRef.current) richEditorRef.current.innerHTML = '';
        setActiveFormats({});
        setShowMentionSuggestions(false);
        setMentionInput("");
        clearAttachments();
        setReplyExiting(null);
        dispatch({ type: RESET_LATEST_MESSAGES, status: true });
      }, 200);
    }
  }, [replyingTo]);

  const parseGlobalSearchParams = useCallback((search) => {
    const params = new URLSearchParams(search || '');
    return {
      open: params.get(GS_PARAM) === '1',
      q: params.get(GQ_PARAM) || '',
      tab: params.get(GTAB_PARAM) || '',
      addr: params.get(GADDR_PARAM) || '',
    };
  }, []);

  const buildGlobalSearchString = useCallback((baseSearch, { open, q, tab, addr }) => {
    const params = new URLSearchParams(baseSearch || '');
    params.delete(GS_PARAM);
    params.delete(GQ_PARAM);
    params.delete(GTAB_PARAM);
    params.delete(GADDR_PARAM);
    if (open && q) {
      params.set(GS_PARAM, '1');
      params.set(GQ_PARAM, q);
      if (tab) params.set(GTAB_PARAM, tab);
      if (addr) params.set(GADDR_PARAM, addr);
    }
    const s = params.toString();
    return s ? `?${s}` : '';
  }, []);

  const syncSearchUrl = useCallback((q, tab, { replace = false, addr } = {}) => {
    const trimmed = String(q || '').trim();
    if (!trimmed) return;
    const nextTab = tab || activeTab;
    const nextAddr = addr !== undefined ? String(addr || '').trim() : String(emailParticipant || '').trim();
    const nextSearch = buildGlobalSearchString(location.search, {
      open: true,
      q: trimmed,
      tab: nextTab,
      addr: nextAddr,
    });
    const key = `${location.pathname}${nextSearch}`;
    const current = parseGlobalSearchParams(location.search);
    if (
      current.open &&
      current.q === trimmed &&
      (current.tab || '') === (nextTab || '') &&
      (current.addr || '') === (nextAddr || '')
    ) {
      lastPushedSearchKeyRef.current = `${location.pathname}${location.search || ''}`;
      return;
    }
    // Same query already in URL (tab may differ only by absence) — just track key.
    if (current.open && current.q === trimmed && !nextTab && !nextAddr) {
      lastPushedSearchKeyRef.current = `${location.pathname}${location.search || ''}`;
      return;
    }
    const useReplace = replace || current.open;
    if (useReplace) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
        state: { ...(location.state || {}), globalSearch: { q: trimmed, tab: nextTab, addr: nextAddr } },
      });
    } else {
      history.push({
        pathname: location.pathname,
        search: nextSearch,
        state: { ...(location.state || {}), globalSearch: { q: trimmed, tab: nextTab, addr: nextAddr } },
      });
    }
    lastPushedSearchKeyRef.current = key;
  }, [activeTab, buildGlobalSearchString, emailParticipant, history, location.pathname, location.search, location.state, parseGlobalSearchParams]);

  const closeSearchPopup = useCallback(() => {
    shouldRestoreScrollRef.current = false;
    pendingScrollRestoreRef.current = null;
    globalPendingSearchScroll = null;
    keepSearchMountedForBackRef.current = false;
    searchMountPathRef.current = '';
    lastPushedSearchKeyRef.current = '';
    // Prevent URL-sync / history restore from immediately re-opening the popup.
    skippingHistoryPushRef.current = true;
    setSearchOverlaySuppressed(false);
    setShowResults(false);
    setEmailParticipant('');
    dispatch({ type: SET_SEARCH_STATUS, payload: false });
    closeReplySection();
    const current = parseGlobalSearchParams(location.search);
    const stateSearch = location.state?.globalSearch;
    if (!current.open && !stateSearch?.q) return;
    // Always strip ?gs/gq/gtab via replace. goBack() often lands on an older
    // history entry that still has ?gs=1, which re-opens an empty-looking popup.
    const stripped = buildGlobalSearchString(location.search, { open: false, q: '', tab: '' });
    const nextState = { ...(location.state || {}) };
    delete nextState.globalSearch;
    history.replace({ pathname: location.pathname, search: stripped, state: nextState });
  }, [buildGlobalSearchString, closeReplySection, dispatch, history, location.pathname, location.search, location.state, parseGlobalSearchParams]);

  const normalizeSearchTab = useCallback((tab) => {
    // Legacy URL tabs for split email buckets → single Email tab.
    if (tab === 'emailAddresses' || tab === 'emailAttachments') return 'emails';
    return tab || 'messages';
  }, []);

  const selectSearchTab = useCallback((tab) => {
    const next = normalizeSearchTab(tab);
    if (tab === 'emailAddresses') setEmailFilterChip('addresses');
    else if (tab === 'emailAttachments') setEmailFilterChip('attachments');
    else if (next === 'emails' && tab === 'emails') {
      /* keep current chip */
    }
    setActiveTab(next);
    if (showResults && searchValue.trim()) {
      syncSearchUrl(searchValue.trim(), next, { replace: true });
    }
  }, [normalizeSearchTab, searchValue, showResults, syncSearchUrl]);

  // Ensure the current history entry is a search URL before pushing a result destination.
  // Fixes Back not restoring when the sync effect never pushed (race / stuck skip flag).
  const ensureSearchHistoryEntry = useCallback(() => {
    const q = String(searchValue || lastFetchedQueryRef.current || '').trim();
    if (!q) return;
    const addr = String(emailParticipant || '').trim();
    const current = parseGlobalSearchParams(location.search);
    if (current.open && current.q === q && (current.addr || '') === addr) {
      lastPushedSearchKeyRef.current = `${location.pathname}${location.search || ''}`;
      return;
    }
    const nextSearch = buildGlobalSearchString(location.search, {
      open: true,
      q,
      tab: activeTab,
      addr,
    });
    history.replace({
      pathname: location.pathname,
      search: nextSearch,
      state: { ...(location.state || {}), globalSearch: { q, tab: activeTab, addr } },
    });
    lastPushedSearchKeyRef.current = `${location.pathname}${nextSearch}`;
  }, [activeTab, buildGlobalSearchString, emailParticipant, history, location.pathname, location.search, location.state, parseGlobalSearchParams, searchValue]);

  const saveResultsScrollPosition = useCallback((resultKey) => {
    const el = resultsListRef.current;
    const top = el ? el.scrollTop : lastScrollTop.current;
    const prev = pendingScrollRestoreRef.current || globalPendingSearchScroll;
    const snapshot = {
      top: Number(top) || 0,
      resultKey: resultKey || prev?.resultKey || null,
    };
    globalPendingSearchScroll = snapshot;
    pendingScrollRestoreRef.current = snapshot;
    // Restore even when top is 0 if we have a result row to scroll into view.
    shouldRestoreScrollRef.current = snapshot.top > 0 || !!snapshot.resultKey;
  }, []);

  const dismissSearchUiKeepHistory = useCallback(() => {
    saveResultsScrollPosition();
    // Keep the results DOM mounted (hidden) so Back returns to the same scroll
    // position without a fragile remount/restore race.
    keepSearchMountedForBackRef.current = true;
    searchMountPathRef.current = location.pathname;
    // Prevent the URL-sync effect from re-pushing ?gs=1 after we navigate to a result
    // (showResults stays true while suppressed, and syncSearchUrl identity changes with location).
    skippingHistoryPushRef.current = true;
    setSearchOverlaySuppressed(true);
    dispatch({ type: SET_SEARCH_STATUS, payload: false });
    closeReplySection();
  }, [closeReplySection, dispatch, location.pathname, saveResultsScrollPosition]);

  const openSearchFromHistory = useCallback((q, tab, addr) => {
    const query = String(q || '').trim();
    if (!query) return;
    const nextAddr = String(addr || '').trim();
    skippingHistoryPushRef.current = true;
    ignoreOutsideClickRef.current = true;
    // If results stayed mounted (only hidden while viewing a result), scroll is still intact.
    const stayedMounted = !!showResultsRef.current;
    keepSearchMountedForBackRef.current = false;
    searchMountPathRef.current = '';
    // Reveal suppressed popup. If it was unmounted, restore from snapshot.
    if (!stayedMounted &&
      globalPendingSearchScroll &&
      (globalPendingSearchScroll.top > 0 || globalPendingSearchScroll.resultKey)
    ) {
      pendingScrollRestoreRef.current = globalPendingSearchScroll;
      shouldRestoreScrollRef.current = true;
    }
    setSearchOverlaySuppressed(false);
    setSearchValue(query);
    setEmailParticipant(nextAddr);
    if (tab) {
      if (tab === 'emailAddresses') setEmailFilterChip('addresses');
      else if (tab === 'emailAttachments') setEmailFilterChip('attachments');
      setActiveTab(normalizeSearchTab(tab));
    }
    setShowResults(true);
    dispatch({ type: SET_SEARCH_STATUS, payload: true });
    // Avoid refetch when we kept results mounted — refetch would rebuild the list and lose scroll.
    const fetchKey = `${query}||${nextAddr}`;
    if (!stayedMounted && lastFetchedQueryRef.current !== fetchKey && searchMessagesRef.current) {
      searchMessagesRef.current(query, 1, nextAddr);
    }
    window.setTimeout(() => {
      ignoreOutsideClickRef.current = false;
    }, 500);
  }, [dispatch, normalizeSearchTab]);

  // Restore search popup from URL (Back/Forward / remount).
  useLayoutEffect(() => {
    const { open, q, tab, addr } = parseGlobalSearchParams(location.search);
    const stateSearch = location.state?.globalSearch;
    if (open && q) {
      openSearchFromHistory(q, tab || stateSearch?.tab || '', addr || stateSearch?.addr || '');
      return;
    }
    // Fallback: history state may carry search even if query string was stripped.
    if (stateSearch?.q && history.action === 'POP') {
      openSearchFromHistory(stateSearch.q, stateSearch.tab || '', stateSearch.addr || '');
      syncSearchUrl(stateSearch.q, stateSearch.tab || activeTab, { replace: true, addr: stateSearch.addr || '' });
      return;
    }
    if (!open && showResultsRef.current) {
      // Opening a search result clears ?gs=1 but we keep the popup mounted (hidden)
      // on the same path so Back can restore scroll instantly.
      if (
        keepSearchMountedForBackRef.current &&
        location.pathname === searchMountPathRef.current
      ) {
        return;
      }
      keepSearchMountedForBackRef.current = false;
      searchMountPathRef.current = '';
      setSearchOverlaySuppressed(false);
      setShowResults(false);
      closeReplySection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore only on location search/path changes
  }, [location.pathname, location.search, location.key]);

  // Push/replace search URL when popup opens with a query.
  // Consume the one-shot skip flag here (do not use setTimeout — StrictMode cleanup can stick it true).
  useEffect(() => {
    if (skippingHistoryPushRef.current) {
      skippingHistoryPushRef.current = false;
      return;
    }
    // While viewing a result the popup stays mounted (hidden). Do not write ?gs=1
    // back onto the URL — that would clear the opened email and re-show search.
    if (keepSearchMountedForBackRef.current || searchOverlaySuppressed) {
      return;
    }
    if (showResults && lastFetchedQueryRef.current) {
      syncSearchUrl(searchValue.trim(), activeTab, { replace: false });
    }
  }, [showResults, activeTab, syncSearchUrl, searchOverlaySuppressed]);

  // Helper: format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Outlook-style compact list date: "Fri 7/31" this year, else locale date.
  const formatEmailListDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const sameYear = date.getFullYear() === now.getFullYear();
    try {
      if (sameYear) {
        return date.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });
      }
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });
    } catch (_) {
      return date.toLocaleDateString();
    }
  };

  const getEmailFolderKey = (emailItem) => {
    const labels = Array.isArray(emailItem?.labels) ? emailItem.labels.map((l) => String(l || '').toUpperCase()) : [];
    if (labels.includes('TRASH') || labels.includes('DELETED')) return 'deleted';
    if (labels.includes('SPAM') || labels.includes('JUNK')) return 'spam';
    if (labels.includes('DRAFT')) return 'drafts';
    if (labels.includes('SENT') || emailItem?.direction === 'outgoing') return 'sent';
    if (labels.includes('ARCHIVE')) return 'archive';
    return 'inbox';
  };

  const getEmailFolderLabel = (emailItem) => {
    const key = getEmailFolderKey(emailItem);
    const map = {
      inbox: strings.email_folder_inbox || 'Inbox',
      sent: strings.email_folder_sent || 'Sent',
      drafts: strings.email_folder_drafts || 'Drafts',
      spam: strings.email_folder_spam || 'Spam',
      deleted: strings.email_folder_deleted || 'Deleted',
      archive: strings.email_folder_archive || 'Archive',
    };
    return map[key] || map.inbox;
  };

  const highlightText = (text, highlight) => {
    if (text == null || text === '') return text;
    const raw = String(text);
    const q = String(highlight || '').trim();
    if (!q) return raw;

    // Highlight the full query and each token so multi-word searches mark every hit.
    const terms = Array.from(
      new Set(
        q.split(/[\s,]+/).filter((t) => {
          if (!t) return false;
          if (t.includes('@')) return t.length >= 3;
          return t.length >= 3;
        })
      )
    ).sort((a, b) => b.length - a.length);
    if (!terms.length) return raw;

    const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    let regex;
    try {
      regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    } catch (_) {
      return raw;
    }

    const lowerTerms = new Set(terms.map((t) => t.toLowerCase()));
    return raw.split(regex).map((part, i) => {
      if (!part) return null;
      if (lowerTerms.has(part.toLowerCase())) {
        return (
          <mark key={i} className="search-highlight-mark">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const globalSearchEmailFromName = (result) => {
    const name = result.message?.fromAddress?.name;
    if (name && String(name).trim()) return String(name).trim();
    const parts = [result.sender?.firstName, result.sender?.lastName].filter(
      (p) => p != null && String(p).trim() !== '' && String(p).toLowerCase() !== 'null'
    );
    if (parts.length) return parts.join(' ');
    const email = result.message?.fromAddress?.email;
    return email || '';
  };

  const globalSearchEmailFromAddress = (result) => {
    const email = result.message?.fromAddress?.email || result.sender?.email;
    return email && String(email).trim() ? String(email).trim() : '';
  };

  const globalSearchEmailToAddressesCsv = (result) => {
    const list = result.message?.toAddresses;
    if (!Array.isArray(list)) return '';
    return list.map((a) => a?.email).filter((e) => e != null && String(e).trim() !== '').join(', ');
  };

  const getEmailAddressMatchInfo = (emailItem, searchTerm) => {
    const q = String(searchTerm || '').trim().toLowerCase();
    const words = q.split(/\s+/).filter((w) => w.length >= 2);
    const fromEmail = String(emailItem?.fromAddress?.email || emailItem?.fromEmail || '').toLowerCase();
    const fromName = String(emailItem?.fromName || emailItem?.fromAddress?.name || '').toLowerCase();
    const toList = Array.isArray(emailItem?.toAddresses) ? emailItem.toAddresses : [];
    const toEmails = toList.map((a) => String(a?.email || '').toLowerCase()).filter(Boolean);
    const toNames = toList.map((a) => String(a?.name || '').toLowerCase()).filter(Boolean);
    const haystacks = [fromEmail, fromName, ...toEmails, ...toNames];
    const matchesQuery = (value) => {
      if (!value) return false;
      if (q && value.includes(q)) return true;
      return words.some((w) => value.includes(w));
    };
    const fromMatched = matchesQuery(fromEmail) || matchesQuery(fromName);
    const toMatched = toEmails.some(matchesQuery) || toNames.some(matchesQuery);
    const isAddressMatch = haystacks.some(matchesQuery);
    return { isAddressMatch, fromMatched, toMatched };
  };

  const emailHasContentMatch = (emailItem, searchTerm) => {
    const q = String(searchTerm || '').trim().toLowerCase();
    if (!q) return false;
    const words = q.split(/\s+/).filter((w) => w.length >= 2);
    const subject = String(emailItem?.subject || '').toLowerCase();
    const body = String(
      emailItem?.bodyText || emailItem?.message || emailItem?.snippet || ''
    ).toLowerCase();
    if (subject.includes(q) || body.includes(q)) return true;
    return words.some((w) => subject.includes(w) || body.includes(w));
  };

  const emailHasAttachmentMatch = (emailItem, searchTerm) => {
    const q = String(searchTerm || '').trim().toLowerCase();
    if (!q) return false;
    const words = q.split(/\s+/).filter((w) => w.length >= 2);
    const names = Array.isArray(emailItem?.attachmentNames)
      ? emailItem.attachmentNames.map((n) => String(n || '').toLowerCase()).filter(Boolean)
      : [];
    const content = String(
      emailItem?.attachmentContentSnippet || emailItem?.attachmentContent || ''
    ).toLowerCase();
    if (names.some((n) => n.includes(q))) return true;
    if (content && content.includes(q)) return true;
    if (words.some((w) => names.some((n) => n.includes(w)))) return true;
    if (content && words.some((w) => content.includes(w))) return true;
    return false;
  };

  /**
   * Search preview body for emails. Prefer plain text fields and lightly trim
   * trailing quoted reply blocks when the new message above them is non-empty.
   * Never returns empty when source text exists (safe fallback to original).
   */
  /** Normalize global-search email hits (nested sourceType shape) or flat inbox-style rows. */
  const mapGlobalSearchEmailToResult = (item) => {
    if (!item) return null;
    const isNested = item.sourceType === 'email' || (item.message && (item.message.fromAddress || item.message.subject != null || item.message.bodyText != null));
    const msg = isNested ? (item.message || {}) : item;
    const fromAddress = msg.fromAddress || item.fromAddress || null;
    const toAddresses = msg.toAddresses || item.toAddresses || null;
    const id = msg.id || item.id;
    if (!id && !msg.subject && !msg.bodyText && !item.subject) return null;
    const attachmentNamesRaw = msg.attachmentNames || msg.attachedFileName || item.attachmentNames || [];
    const attachmentNames = Array.isArray(attachmentNamesRaw)
      ? attachmentNamesRaw.filter(Boolean)
      : (attachmentNamesRaw ? [attachmentNamesRaw] : []);
    const labels = Array.isArray(msg.labels) ? msg.labels : (Array.isArray(item.labels) ? item.labels : []);
    const direction = msg.direction || item.direction || null;
    return {
      id,
      subject: msg.subject ?? item.subject ?? '',
      bodyText: msg.bodyText || msg.message || item.bodyText || item.message || '',
      message: msg.message || msg.bodyText || item.message || item.bodyText || '',
      snippet: msg.snippet || item.snippet || '',
      body: msg.body || item.body,
      bodyHtml: msg.bodyHtml || item.bodyHtml,
      fromName: fromAddress?.name || item.fromName || item.sender?.firstName || '',
      fromEmail: fromAddress?.email || item.fromEmail || item.sender?.email || '',
      fromAddress,
      toAddresses,
      receivedAt: msg.receivedAt || item.receivedAt || null,
      attachmentNames,
      attachmentContentSnippet: msg.attachmentContentSnippet || item.attachmentContentSnippet || null,
      hasAttachments: attachmentNames.length > 0 || !!(msg.hasAttachments || item.hasAttachments),
      attachments: Array.isArray(msg.attachments)
        ? msg.attachments
        : (Array.isArray(item.attachments) ? item.attachments : []),
      emailAccountId: msg.emailAccountId || item.emailAccountId || null,
      isRead: typeof msg.isRead === 'boolean' ? msg.isRead : item.isRead,
      providerMessageId: msg.providerMessageId || item.providerMessageId || null,
      labels,
      direction,
      _matchContent: false,
      _matchAddress: false,
      _matchAttachment: false,
    };
  };

  const getEmailSearchPreviewText = (message) => {
    const raw = String(
      message?.bodyText ||
      message?.message ||
      message?.snippet ||
      (typeof message?.body === 'string' ? message.body : '') ||
      ''
    );
    if (!raw.trim()) return '';

    let t = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Cut only at clear reply/forward markers that start a new line.
    // French: require weekday abbrev (Le mar. / Le lun. …) so prose like
    // "Le devis …" is never treated as a quote header.
    const cutPatterns = [
      /\nOn\s(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b[\s\S]{0,180}?wrote:\s*(?:\n|$)/i,
      /\nAm\s\d{1,2}\.[\s\S]{0,180}?schrieb[\s\S]{0,80}?:\s*(?:\n|$)/i,
      /\nLe\s+(?:lun|mar|mer|jeu|ven|sam|dim)\.?\s[\s\S]{0,200}?\ba\s*\n?\s*écrit\s*:\s*(?:\n|$)/i,
      /\n-+\s*Forwarded message\s*-+/i,
      /\n-+\s*Message transféré\s*-+/i,
      /\n-+\s*Original Message\s*-+/i,
      /\n-+\s*Urspr[üu]ngliche Nachricht\s*-+/i,
      /\nBegin forwarded message:\s*(?:\n|$)/i,
    ];

    let cutAt = -1;
    for (const pattern of cutPatterns) {
      const match = t.match(pattern);
      if (match && match.index != null && match.index > 0) {
        const before = t.slice(0, match.index).trim();
        // Only cut if there is real message content before the quote marker
        if (before.length >= 2 && (cutAt < 0 || match.index < cutAt)) {
          cutAt = match.index;
        }
      }
    }
    if (cutAt > 0) t = t.slice(0, cutAt);

    // Stop at the first contiguous `>` quote block if we already kept content
    const lines = t.split('\n');
    const kept = [];
    for (const line of lines) {
      if (/^\s*>/.test(line)) {
        if (kept.some((l) => l.trim())) break;
        continue;
      }
      kept.push(line);
    }
    const cleaned = kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    return cleaned || raw.trim();
  };

  /** Map global search email hit to pending-email payload; opens detail from prefetched fields (inbox may not list the message yet). */
  const buildEmailNavigatePayloadFromGlobalSearch = (result) => {
    const msg = result.message || {};
    const id =
      msg.id ??
      msg._id ??
      msg.messageId ??
      msg.providerMessageId;
    const rawBody = msg.body;
    const bodyIsHtml = typeof rawBody === 'string' && looksLikeHtml(rawBody);
    const plainBody =
      msg.bodyText ||
      (typeof rawBody === 'string' && rawBody.trim() && !bodyIsHtml ? rawBody : '') ||
      '';
    const toLine =
      (Array.isArray(msg.toAddresses) &&
        msg.toAddresses
          .map((a) => a?.email || a?.name)
          .filter((x) => x != null && String(x).trim() !== '')
          .join(', ')) ||
      msg.to ||
      '';
    const prefetchedEmail = {
      id,
      _id: msg._id ?? id,
      messageId: msg.messageId,
      providerMessageId: msg.providerMessageId,
      subject: msg.subject,
      body: typeof rawBody === 'string' ? rawBody : plainBody,
      bodyText: msg.bodyText != null ? msg.bodyText : plainBody,
      bodyHtml: msg.bodyHtml,
      snippet: msg.snippet,
      fromAddress: msg.fromAddress,
      toAddresses: msg.toAddresses,
      to: toLine || undefined,
      receivedAt: msg.receivedAt || msg.createdAt,
      attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
      attachmentNames: Array.isArray(msg.attachmentNames)
        ? msg.attachmentNames.filter(Boolean)
        : (Array.isArray(result.attachmentNames) ? result.attachmentNames.filter(Boolean) : []),
      hasAttachments: !!(
        msg.hasAttachments
        || (Array.isArray(msg.attachments) && msg.attachments.length > 0)
        || (Array.isArray(msg.attachmentNames) && msg.attachmentNames.length > 0)
        || (Array.isArray(result.attachmentNames) && result.attachmentNames.length > 0)
      ),
      isRead: msg.isRead,
      fromName: msg.fromAddress?.name ?? msg.fromName,
      from: msg.fromAddress?.email ?? msg.from,
    };
    return {
      ...prefetchedEmail,
      emailAccountId: msg.emailAccountId ?? result.emailAccountId,
      fromGlobalSearch: true,
      prefetchedEmail,
    };
  };

  // Toggle message expansion for ...more / ...less
  const toggleMessageExpansion = (messageId) => {
    setExpandedMessages(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // API: search messages and groups (with searchId for race condition fix)
  const searchMessages = async (searchTerm, pageNum = 1, participantArg) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setGroupResults([]);
      setRoomResults([]);
      setUserResults([]);
      setEmailResults([]);
      setEmailFilterChip('all');
      setDocumentResults([]);
      setTodoResults([]);
      setIsTyping(false);
      closeSearchPopup();
      return;
    }
    const intent = parseSearchIntent(
      searchTerm,
      participantArg !== undefined ? participantArg : emailParticipant
    );
    setIsLoading(true);
    setIsGroupsLoading(true);
    setIsRoomsLoading(true);
    setIsUsersLoading(true);
    setIsEmailsLoading(true);
    setShowResults(true);
    setSearchOverlaySuppressed(false);
    const currentSearchId = ++searchIdRef.current;
    try {
      const requestParams = {
        search: searchTerm,
        page: pageNum,
        limit: 50,
        // Microsoft Teams chats in the hits (messages, chat groups, documents),
        // when the user has Teams connected.
        includeTeams: 1,
      };
      if (intent.intent === 'email' && intent.active) {
        requestParams.emailParticipant = intent.person;
        requestParams.search = intent.content || intent.q || searchTerm;
      }
      if (intent.intent === 'chat' && intent.active) {
        requestParams.chatSender = intent.person;
        requestParams.search = intent.content || intent.q || searchTerm;
      }
      const preferredTab =
        intent.intent === 'email' && intent.active
          ? 'emails'
          : intent.intent === 'chat' && intent.active
            ? 'messages'
            : intent.intent === 'user'
              ? 'users'
              : null;
      const { data } = await callAPI(
        '/group/searchMessageGlobally',
        requestParams,
        'GET'
      );
      if (searchIdRef.current === currentSearchId) {
        if (data.status) {
          const rawMessageData = data.messageData || [];
          // Keep Messages tab chat-only; emails belong on the Email tab.
          const scopedEmail = intent.intent === 'email' && intent.active;
          const scopedChat = intent.intent === 'chat' && intent.active;
          const scopedUser = intent.intent === 'user';
          let chatResults = scopedEmail || scopedUser
            ? []
            : rawMessageData.filter((r) => r?.sourceType !== 'email');
          const emailsFromMessageData = scopedEmail
            ? []
            : rawMessageData.filter((r) => r?.sourceType === 'email');

          // Newest-first helper (latest on top). Used when BE sort mode is chronological.
          const dateMs = (value) => {
            const ms = new Date(value || 0).getTime();
            return Number.isFinite(ms) ? ms : 0;
          };
          const sortNewestFirst = (list, getDate) =>
            [...list].sort((a, b) => dateMs(getDate(b)) - dateMs(getDate(a)));

          chatResults = sortNewestFirst(
            chatResults,
            (r) => r.message?.createdAt || r.message?.receivedAt
          );

          setSearchResults((prev) => (pageNum === 1 ? chatResults : [...prev, ...chatResults]));
          setHasMore(chatResults.length >= 20);
          setShowResults(true);
          if (pageNum === 1) {
            lastFetchedQueryRef.current = `${searchTerm.trim()}||${intent.person || ''}`;
            // Fresh search (not Back-restore): start list at top.
            if (!shouldRestoreScrollRef.current && resultsListRef.current) {
              resultsListRef.current.scrollTop = 0;
              lastScrollTop.current = 0;
              setIsTabsHidden(false);
            }
            // Eager URL sync so Back has a search entry even if the effect races.
            if (!skippingHistoryPushRef.current) {
              syncSearchUrl(searchTerm.trim(), preferredTab || activeTab, {
                replace: false,
                addr: String(participantArg !== undefined ? participantArg : emailParticipant || '').trim(),
              });
            }
          }
          if (chatResults.length < 20) setHasMore(false);

          // Get groups, rooms, users, emails from the same API response (only on first page)
          if (pageNum === 1) {
            let groups = scopedEmail || scopedChat || scopedUser
              ? []
              : (data.groups || []).filter((g) => !g.isArchived);
            let rooms = scopedEmail || scopedChat || scopedUser ? [] : (data.rooms || []);
            let users_data = scopedEmail || scopedChat ? [] : (data.users || []);
            let docs_data = scopedEmail || scopedChat || scopedUser ? [] : (data.documents || []);
            let todos_data = scopedEmail || scopedChat || scopedUser ? [] : (data.todos || []);
            // Merge Content / Addresses / Attachments into one Outlook-style list.
            const emailKey = (e) => String(e?.id || `${e?.subject}|${e?.receivedAt}|${e?.fromEmail}`);
            const mappedByKey = new Map();
            const upsertEmail = (raw, flags = {}) => {
              const mapped = mapGlobalSearchEmailToResult(raw);
              if (!mapped) return;
              const key = emailKey(mapped);
              const prev = mappedByKey.get(key);
              if (prev) {
                mappedByKey.set(key, {
                  ...prev,
                  ...mapped,
                  _matchContent: !!(prev._matchContent || flags.content || mapped._matchContent),
                  _matchAddress: !!(prev._matchAddress || flags.address || mapped._matchAddress),
                  _matchAttachment: !!(prev._matchAttachment || flags.attachment || mapped._matchAttachment),
                  attachmentNames: (mapped.attachmentNames?.length ? mapped.attachmentNames : prev.attachmentNames) || [],
                  attachmentContentSnippet: mapped.attachmentContentSnippet || prev.attachmentContentSnippet || null,
                  labels: (mapped.labels?.length ? mapped.labels : prev.labels) || [],
                  direction: mapped.direction || prev.direction || null,
                  isRead: typeof mapped.isRead === 'boolean' ? mapped.isRead : prev.isRead,
                });
                return;
              }
              mappedByKey.set(key, {
                ...mapped,
                _matchContent: !!flags.content,
                _matchAddress: !!flags.address,
                _matchAttachment: !!flags.attachment,
              });
            };

            const contentRaw = [
              ...(Array.isArray(data.emails) ? data.emails : []),
              ...emailsFromMessageData,
            ];
            contentRaw.forEach((raw) => upsertEmail(raw, { content: true }));
            (Array.isArray(data.emailAddresses) ? data.emailAddresses : []).forEach((raw) =>
              upsertEmail(raw, { address: true })
            );
            (Array.isArray(data.emailAttachments) ? data.emailAttachments : []).forEach((raw) =>
              upsertEmail(raw, { attachment: true })
            );

            // Recompute match flags from the query so chips stay accurate even
            // when BE buckets overlap or a legacy response omits a bucket.
            const matchQuery = intent.content || searchTerm;
            let emails_data = Array.from(mappedByKey.values()).map((e) => {
              const { isAddressMatch } = getEmailAddressMatchInfo(e, matchQuery);
              const participantMatch = !scopedEmail && intent.active && intent.person
                ? getEmailAddressMatchInfo(e, intent.person).isAddressMatch
                : false;
              return {
                ...e,
                _matchContent: e._matchContent || emailHasContentMatch(e, matchQuery) || scopedEmail,
                _matchAddress: e._matchAddress || isAddressMatch || participantMatch,
                _matchAttachment: e._matchAttachment || emailHasAttachmentMatch(e, matchQuery),
              };
            });
            // Keep rows that match in at least one dimension (or came from BE).
            // Combination results are already AND-filtered on the server.
            if (!(intent.active && intent.intent === 'email')) {
              emails_data = emails_data.filter(
                (e) => e._matchContent || e._matchAddress || e._matchAttachment
              );
            }
            emails_data = sortNewestFirst(emails_data, (e) => e.receivedAt);
            if (scopedChat || scopedUser) emails_data = [];

            setGroupResults(groups);
            setRoomResults(rooms);
            setUserResults(users_data);
            setEmailResults(emails_data);
            setEmailFilterChip(scopedEmail ? 'content' : 'all');
            setDocumentResults(docs_data);
            setTodoResults(todos_data);
            setActiveTab((prev) => {
              if (preferredTab) return preferredTab;
              const normalizedPrev = normalizeSearchTab(prev);
              const tabDataMap = {
                messages: chatResults,
                groups,
                rooms,
                users: users_data,
                emails: emails_data,
                documents: docs_data,
                todos: todos_data,
              };
              const arr = tabDataMap[normalizedPrev];
              if (!arr || arr.length === 0) {
                if (emails_data.length > 0) return 'emails';
                if (chatResults.length > 0) return 'messages';
                if (groups.length > 0) return 'groups';
                if (rooms.length > 0) return 'rooms';
                if (users_data.length > 0) return 'users';
                if (docs_data.length > 0) return 'documents';
                if (todos_data.length > 0) return 'todos';
              }
              return normalizedPrev;
            });
          }
        } else {
          setSearchResults([]);
          setGroupResults([]);
          setRoomResults([]);
          setUserResults([]);
          setEmailResults([]);
          setEmailFilterChip('all');
          setDocumentResults([]);
          setTodoResults([]);
          setHasMore(false);
          setShowResults(true);
        }
      }
    } catch (error) {
      if (searchIdRef.current === currentSearchId) {
        setSearchResults([]);
        setGroupResults([]);
        setRoomResults([]);
        setUserResults([]);
        setEmailResults([]);
        setEmailFilterChip('all');
        setDocumentResults([]);
        setTodoResults([]);
        setShowResults(true);
        setHasMore(false);
      }
      console.error('Search error:', error);
    } finally {
      if (searchIdRef.current === currentSearchId) {
        setIsLoading(false);
        setIsGroupsLoading(false);
        setIsRoomsLoading(false);
        setIsUsersLoading(false);
        setIsEmailsLoading(false);
        setIsTyping(false);
      }
    }
  };
  searchMessagesRef.current = searchMessages;

  // API: fetch group secret
  const fetchGroupSecret = async (groupId) => {
    try {
      let getGroupSecretObj = {
        url: `${SOCKET_BASE}/group/${groupId}/getGroupSecret`,
        method: 'get',
        headers: {
          Authorization: `jwt ${$user.getToken()}`
        },
      };
      socket.emit('get', getGroupSecretObj, (response) => {
        console.log('Group secret response:', response);
        if (response?.body?.status && response.body.groupInfo) {
          setGroupSecret(response.body.groupInfo.secretKey);
        }
      });
    } catch (error) {
      console.error('Error fetching group secret:', error);
    }
  };

  // API: fetch group members for @mentions
  const fetchGroupMembers = async (groupId) => {
    try {
      console.log('Fetching group members for groupId:', groupId);
      const { data } = await callAPI(`/group/${groupId}/info`, {}, "GET");
      console.log('Group info response:', data);
      if (data.status && data.groupInfo?.users) {
        let mems = data.groupInfo.users.filter(
          (u) => u.id !== user?.id && !u.isLeave
        );
        mems = mems.map((e) => ({
          id: e.id,
          display: e.fullName,
          name: e.fullName,
          avatar: e.avatar,
        }));
        // Add "All" option at the beginning if there are members
        if (mems.length > 0) {
          mems.unshift({
            id: "idall12322",
            display: strings.All || 'All',
            name: strings.All || 'All',
            type: "All",
          });
        }
        console.log('Setting mentionSuggestions:', mems);
        setMentionSuggestions(mems);
      }
    } catch (error) {
      console.error('Error fetching group members:', error);
    }
  };

  // API: reply to message
  const replyToMessage = async (result) => {
    let uniID = makeid(10);
    const replyBody = (getRichEditorContent() || replyText || "");
    setReplyLoading(true);
    try {
      // 1. Document reply
      if (selectedDoc) {
        const fileName = selectedDoc.name;
        const file = selectedDoc;
        const fileType = fileName.split('.').pop();
        const base64 = await convertBase64(file);
        const formData = new FormData();
        formData.append('id', result.message.id);
        formData.append('group', result.groupData.groupId);
        formData.append('isGroup', 'true');
        formData.append('message', replyBody || '');
        formData.append('messageType', 'doc');
        formData.append('messageRequestId', uniID);
        formData.append('sender', user.id);
        formData.append('attachedFileName', fileName);
        formData.append('file', file);
        const response = await callAPI(`/message/${result.message.id}/reply`, {}, 'POST', formData);
        if (response.data.status) {
          $crud.notify({
            type: "success",
            message: strings.reply_sent_successfully,
          });
          // Clear search and close results
          setSearchValue('');
          setSearchResults([]);
          lastFetchedQueryRef.current = '';
          closeSearchPopup();
        } else {
          $crud.notify({
            type: "error",
            message: response.data.message || (strings.reply_failed),
          });
        }
        setSelectedDoc(null);
        setReplyText('');
        setGroupSecret(null);
        return;
      }
      // 2. Image reply
      if (selectedImage) {
        const file = selectedImage.file;
        const fileName = file.name;
        const formData = new FormData();
        formData.append('id', result.message.id);
        formData.append('group', result.groupData.groupId);
        formData.append('isGroup', 'true');
        formData.append('message', replyBody || '');
        formData.append('messageType', 'image');
        formData.append('messageRequestId', uniID);
        formData.append('sender', user.id);
        formData.append('attachedFileName', fileName);
        formData.append('file', file);
        const response = await callAPI(`/message/${result.message.id}/reply`, {}, 'POST', formData);
        if (response.data.status) {
          $crud.notify({
            type: "success",
            message: strings.reply_sent_successfully,
          });
          // Clear search and close results
          setSearchValue('');
          setSearchResults([]);
          lastFetchedQueryRef.current = '';
          closeSearchPopup();
        } else {
          $crud.notify({
            type: "error",
            message: response.data.message || (strings.reply_failed),
          });
        }
        setSelectedImage(null);
        setReplyText('');
        setGroupSecret(null);
        return;
      }
      // 2. Multiple Images reply
      if (selectedImages.length > 0) {
        try {
          // Process each image
          for (let i = 0; i < selectedImages.length; i++) {
            const img = selectedImages[i];
            const file = img.file;
            const fileName = file.name;
            const formData = new FormData();
            formData.append('id', result.message.id);
            formData.append('group', result.groupData.groupId);
            formData.append('isGroup', 'true');
            formData.append('message', replyBody || '');
            formData.append('messageType', 'image');
            formData.append('messageRequestId', uniID);
            formData.append('sender', user.id);
            formData.append('attachedFileName', fileName);
            formData.append('file', file);

            const response = await callAPI(`/message/${result.message.id}/reply`, {}, 'POST', formData);
            if (!response.data.status) {
              throw new Error(response.data.message || 'Failed to send image');
            }
          }

          $crud.notify({
            type: "success",
            message: strings.reply_sent_successfully,
          });
          // Clear search and close results
          setSearchValue('');
          setSearchResults([]);
          lastFetchedQueryRef.current = '';
          closeSearchPopup();
        } catch (error) {
          $crud.notify({
            type: "error",
            message: error.message || (strings.reply_failed),
          });
        }
        setSelectedImages([]);
        setReplyText('');
        setGroupSecret(null);
        return;
      }
      // 3. Audio reply
      if (recordedAudio) {
        // Create a File from the blob
        const fileName = `audio_${Date.now()}.webm`;
        const audioFile = new File([recordedAudio.blob], fileName, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('id', result.message.id);
        formData.append('group', result.groupData.groupId);
        formData.append('isGroup', 'true');
        formData.append('message', replyBody || '');
        formData.append('messageType', 'audio');
        formData.append('messageRequestId', uniID);
        formData.append('sender', user.id);
        formData.append('attachedFileName', fileName);
        formData.append('file', audioFile);
        const response = await callAPI(`/message/${result.message.id}/reply`, {}, 'POST', formData);
        if (response.data.status) {
          $crud.notify({
            type: "success",
            message: strings.reply_sent_successfully,
          });
          // Clear search and close results
          setSearchValue('');
          setSearchResults([]);
          lastFetchedQueryRef.current = '';
          closeSearchPopup();
        } else {
          $crud.notify({
            type: "error",
            message: response.data.message || (strings.reply_failed),
          });
        }
        removeAudio();
        setReplyText('');
        setGroupSecret(null);
        return;
      }
      // 4. Text reply (existing logic)
      const richContent = replyBody;
      if (!richContent.trim()) return;
      const payload = {
        group: result.groupData.groupId,
        isGroup: true,
        message: groupSecret ? encryptMessage(richContent, groupSecret) : richContent,
        messageType: 'simple',
        sender: user.id,
        messageRequestId: uniID,
        inReplyTo: result.message.id,
        Rid: result.message.id,
        secretKey: groupSecret
      };
      const response = await callAPI(`/message/${result.message.id}/reply`, {}, 'POST', payload);
      if (response.data.status) {
        $crud.notify({
          type: "success",
          message: strings.reply_sent_successfully,
        });
        // Clear search and close results
        setSearchValue('');
        setSearchResults([]);
        lastFetchedQueryRef.current = '';
        closeSearchPopup();
      } else {
        $crud.notify({
          type: "error",
          message: response.data.message || (strings.reply_failed),
        });
      }
      setReplyText('');
      setGroupSecret(null);
    } catch (error) {
      $crud.notify({
        type: "error",
        message: strings.reply_failed_generic,
      });
      console.error('Reply error:', error);
    } finally {
      setReplyLoading(false);
    }
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchValue(searchTerm);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    if (searchTerm.length === 0) {
      setSearchResults([]);
      setGroupResults([]);
      setUserResults([]);
      setEmailParticipant('');
      setIsTyping(false);
      lastFetchedQueryRef.current = '';
      closeSearchPopup();
    }
  };

  const submitSearch = () => {
    const term = String(searchValue || '').trim();
    if (term.length < 3) return;
    shouldRestoreScrollRef.current = false;
    pendingScrollRestoreRef.current = null;
    globalPendingSearchScroll = null;
    keepSearchMountedForBackRef.current = false;
    setSearchOverlaySuppressed(false);
    setShowResults(true);
    setPage(1);
    setHasMore(true);
    searchMessages(term, 1, emailParticipant);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
    }
  };

  // Pagination: only increment page, let useEffect handle API call
  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    
    // Hide tabs on scroll down, show on scroll up
    if (scrollTop > lastScrollTop.current && scrollTop > 50) {
      setIsTabsHidden(true);
    } else {
      setIsTabsHidden(false);
    }
    lastScrollTop.current = scrollTop;
    // Do not overwrite the Back-restore snapshot while a restore is in progress
    // (programmatic scrollTop / scrollIntoView would otherwise wipe resultKey / top).
    if (shouldRestoreScrollRef.current) return;
    if (showResultsRef.current && !searchOverlaySuppressed) {
      const prev = pendingScrollRestoreRef.current || globalPendingSearchScroll;
      const snapshot = { top: scrollTop, resultKey: prev?.resultKey || null };
      pendingScrollRestoreRef.current = snapshot;
      globalPendingSearchScroll = snapshot;
    }
    
    // const { scrollTop, scrollHeight, clientHeight } = e.target;
    // if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isLoading && hasMore) {
    //   setPage(prev => {
    //     if (hasMore) return prev + 1;
    //     return prev;
    //   });
    // }
  };

  // Restore scroll after Back returns to the search popup (list remounts at 0).
  // Retries across frames: autofocus / layout / sticky header can reset scrollTop after the first paint.
  useLayoutEffect(() => {
    if (!showResults || !shouldRestoreScrollRef.current) return;
    const pending = pendingScrollRestoreRef.current || globalPendingSearchScroll;
    const savedTop = pending?.top;
    const resultKey = pending?.resultKey;
    if ((savedTop == null || savedTop <= 0) && !resultKey) {
      shouldRestoreScrollRef.current = false;
      pendingScrollRestoreRef.current = null;
      globalPendingSearchScroll = null;
      return;
    }

    let cancelled = false;
    const timeoutIds = [];
    let rafId = 0;

    const focusPopupInputWithoutScroll = () => {
      const input = popupInputRef.current;
      if (!input || typeof input.focus !== 'function') return;
      try {
        input.focus({ preventScroll: true });
      } catch (err) {
        input.focus();
      }
    };

    const apply = () => {
      if (cancelled) return false;
      const node = resultsListRef.current;
      if (!node) return false;
      const stillLoading = isLoading || isEmailsLoading || isGroupsLoading || isRoomsLoading || isUsersLoading;

      // Prefer bringing the opened result back into view (stable across layout changes).
      if (resultKey) {
        const row = node.querySelector(`[data-search-result-key="${resultKey.replace(/"/g, '\\"')}"]`);
        if (row) {
          row.scrollIntoView({ block: 'center', inline: 'nearest' });
          lastScrollTop.current = node.scrollTop;
          if (node.scrollTop > 50) setIsTabsHidden(true);
          focusPopupInputWithoutScroll();
          return true;
        }
        if (stillLoading) return false;
      }

      if (savedTop == null || savedTop <= 0) return !stillLoading;
      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      // Content may still be loading / short — keep retrying until tall enough or loads finish.
      if (maxScroll + 1 < savedTop && stillLoading) {
        return false;
      }
      const target = Math.min(savedTop, maxScroll);
      node.scrollTop = target;
      lastScrollTop.current = node.scrollTop;
      if (node.scrollTop > 50) setIsTabsHidden(true);
      focusPopupInputWithoutScroll();
      // Confirm it stuck (focus/layout may have reset it).
      return Math.abs(node.scrollTop - target) <= 2 || (!stillLoading && maxScroll < savedTop);
    };

    const finish = () => {
      shouldRestoreScrollRef.current = false;
      pendingScrollRestoreRef.current = null;
      globalPendingSearchScroll = null;
    };

    focusPopupInputWithoutScroll();
    apply();
    // Re-apply after focus/animation/layout; autofocus used to snap the list back to top.
    [16, 50, 120, 250, 400].forEach((ms) => {
      timeoutIds.push(window.setTimeout(() => {
        if (cancelled) return;
        apply();
        if (ms >= 400) finish();
      }, ms));
    });
    rafId = requestAnimationFrame(() => {
      apply();
    });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [
    showResults,
    searchValue,
    activeTab,
    emailFilterChip,
    searchResults.length,
    emailResults.length,
    groupResults.length,
    roomResults.length,
    userResults.length,
    documentResults.length,
    todoResults.length,
    isLoading,
    isEmailsLoading,
    isGroupsLoading,
    isRoomsLoading,
    isUsersLoading,
  ]);

  // Call searchMessages when page changes (but not on first render)
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    if (page > 1) {
      searchMessages(searchValue, page, emailParticipant);
    }
    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ignoreOutsideClickRef.current) return;
      // Popup is only hidden (kept mounted for Back) — don't treat email/detail
      // clicks as "outside" closes while suppressed.
      if (keepSearchMountedForBackRef.current || searchOverlaySuppressed) return;
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        closeSearchPopup();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeSearchPopup, searchOverlaySuppressed]);

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await getVoiceMediaStream();
      mediaStreamRef.current = stream; // Store the stream
      // Use supported MIME type
      let options = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'audio/webm' };
      }
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorderRef.current.mimeType });
        if (audioBlob.size === 0) {
          setRecordedAudio(null);
          setAudioDuration(0);
          return;
        }
        const base64 = await convertBase64(audioBlob);
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio({
          blob: audioBlob,
          url: audioUrl,
          base64: base64
        });
        setTimeout(() => {
          if (audioPreviewRef.current) {
            setAudioDuration(audioPreviewRef.current.duration);
          }
        }, 200); // Wait for audio to load
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setAudioDuration(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordingIntervalRef.current);
      setIsRecording(false);
      // Release the microphone
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
      // Blob and URL will be created in onstop
    }
  };

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const sendAudioFile = async (audio, chatId, base64) => {
    try {
      const formData = new FormData();
      formData.append('file', audio, 'audio.webm');
      formData.append('chatId', chatId);
      formData.append('message', '');
      formData.append('messageType', 'audio');
      formData.append('isGroup', true);
      formData.append('sender', user.id);
      formData.append('messageRequestId', makeid(10));
      formData.append('secretKey', groupSecret);

      await callAPI('/message', {}, 'POST', formData);
      closeReplySection();
      setReplyText('');
    } catch (error) {
      console.error('Error sending audio:', error);
    }
  };

  // Add this function after other helper functions
  const getFileIcon = (fileType) => {
    const icons = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      txt: '📄',
      xls: '📊',
      xlsx: '📊',
      ppt: '📑',
      pptx: '📑',
      csv: '📊',
      default: '📎'
    };
    return icons[fileType?.toLowerCase()] || icons.default;
  };

  // Add this function after other helper functions
  const getImagePreview = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Add this function to handle attachment errors
  const showAttachmentError = (message) => {
    setAttachmentError(message);
    if (attachmentErrorTimeout.current) {
      clearTimeout(attachmentErrorTimeout.current);
    }
    attachmentErrorTimeout.current = setTimeout(() => {
      setAttachmentError(null);
    }, 2000);
  };

  // Add this function to check if any attachment is selected
  const hasAttachment = () => {
    return selectedDoc || selectedImage || selectedImages.length > 0 || isRecording || recordedAudio;
  };

  const hasAttachmentBox = () => {
    return selectedDoc || isRecording || recordedAudio;
  };

  // Add this function to clear all attachments
  const clearAttachments = () => {
    setSelectedDoc(null);
    setSelectedImage(null);
    setSelectedImages([]);
    if (isRecording) {
      stopRecording();
    }
    if (recordedAudio) {
      removeAudio();
    }
  };

  // Add audio playback functions
  const playAudio = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.play();
    }
  };

  const removeAudio = () => {
    if (recordedAudio?.url) {
      URL.revokeObjectURL(recordedAudio.url);
    }
    setRecordedAudio(null);
  };

  const composerMarkdownToHtml = useCallback((md) => sharedComposerMarkdownToHtml(md), []);
  const htmlToMarkdown = useCallback((html) => sharedHtmlToMarkdown(html), []);

  const getRichEditorContent = useCallback(() => {
    const editor = richEditorRef.current;
    if (!editor) return "";
    return htmlToMarkdown(editor.innerHTML);
  }, [htmlToMarkdown]);

  const updateActiveFormats = useCallback(() => {
    try {
      applyActiveFormatsIfChanged(setActiveFormats, activeFormatsRef, {
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        unordered: document.queryCommandState("insertUnorderedList"),
        ordered: document.queryCommandState("insertOrderedList"),
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (replyingTo === null) return;
    document.addEventListener("selectionchange", updateActiveFormats);
    return () => document.removeEventListener("selectionchange", updateActiveFormats);
  }, [replyingTo, updateActiveFormats]);

  const applyComposerFormat = useCallback((formatType) => {
    const editor = richEditorRef.current;
    if (!editor) return;
    editor.focus();
    switch (formatType) {
      case "bold":
        document.execCommand("bold", false, null);
        break;
      case "italic":
        document.execCommand("italic", false, null);
        break;
      case "underline":
        document.execCommand("underline", false, null);
        break;
      case "strikethrough":
        document.execCommand("strikeThrough", false, null);
        break;
      case "unordered":
        document.execCommand("insertUnorderedList", false, null);
        break;
      case "ordered":
        document.execCommand("insertOrderedList", false, null);
        break;
      default:
        break;
    }
  }, []);

  const onMentionSelect = useCallback((mention) => {
    if (mentionCursorRef.current) {
      const editor = richEditorRef.current;
      if (editor) {
        const { node, offset, atIndex } = mentionCursorRef.current;
        if (node && node.parentNode && editor.contains(node)) {
          const before = node.textContent.substring(0, atIndex);
          const after = node.textContent.substring(offset);
          const mentionSpan = document.createElement("span");
          mentionSpan.className = "mention-tag";
          mentionSpan.setAttribute("data-mention-id", mention.id);
          mentionSpan.setAttribute("data-mention-name", mention.name);
          mentionSpan.contentEditable = "false";
          mentionSpan.textContent = `@${mention.name}`;
          const parent = node.parentNode;
          const beforeText = document.createTextNode(before);
          const afterText = document.createTextNode("\u00A0" + after);
          parent.insertBefore(beforeText, node);
          parent.insertBefore(mentionSpan, node);
          parent.insertBefore(afterText, node);
          parent.removeChild(node);
          const sel = window.getSelection();
          const newRange = document.createRange();
          newRange.setStart(afterText, 1);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
          const md = getRichEditorContent();
          setReplyText(md);
        }
      }
      mentionCursorRef.current = null;
    }
    setMentionInput("");
    setShowMentionSuggestions(false);
  }, [getRichEditorContent]);

  const handleRichEditorInput = useCallback(() => {
    const editor = richEditorRef.current;
    if (!editor) return;
    syncComposerEmptiness(setReplyText, editor.innerText);
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editor.contains(range.startContainer) && range.startContainer.nodeType === Node.TEXT_NODE) {
        const text = range.startContainer.textContent;
        const cursorPos = range.startOffset;
        const textBeforeCursor = text.substring(0, cursorPos);
        const atIdx = textBeforeCursor.lastIndexOf("@");
        if (atIdx >= 0) {
          const chBefore = atIdx > 0 ? textBeforeCursor[atIdx - 1] : null;
          if (atIdx === 0 || chBefore === " " || chBefore === "\n" || chBefore === "\xa0") {
            const query = textBeforeCursor.substring(atIdx + 1);
            if (query.length <= 50) {
              setMentionInput(query);
              setShowMentionSuggestions(true);
              mentionCursorRef.current = { node: range.startContainer, offset: cursorPos, atIndex: atIdx };
              return;
            }
          }
        }
      }
    }
    setShowMentionSuggestions(false);
  }, [getRichEditorContent]);

  const navigateToChat = (result) => {
    ensureSearchHistoryEntry();
    dismissSearchUiKeepHistory();
    // Determine if this is an internal chat based on roomType
    const isInternalChat = !!result.groupData.roomType;
    dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: isInternalChat })
    let chatGroupId = result.groupData.groupId;
    let roomId = result.groupData.roomId;
    let companyId = result.groupData.companyId;
    console.log('result.message', result)
    // A Microsoft Teams chat (no room): Messages opens it in the room-less pane in its Teams mode,
    // never a room lookup with its id.
    if (!roomId && chatGroupId && isTeamsTarget(result, chatGroupId)) {
      dispatch({ type: SET_DEEPLINK_LOADING, loading: false });
      dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
      const known = knownTeamsChat(chatGroupId) || {};
      openChatMessage(dispatch, history, {
        chatGroupId: String(chatGroupId),
        messageId: result.message?.id ? String(result.message.id) : null,
        page_no: result.message?.page_no || result.page_no || 1,
        isOneToOne: false,
        name: known.name || result.groupData.groupName || "",
        isTeams: true,
        teamsChatType: known.teamsChatType || result.groupData.teamsChatType || null,
        webUrl: known.webUrl || null,
      });
      return;
    }
    try {
      // Always reset todo view first so that navigating from /todo (or any view that
      // had showTodoView=true) doesn't leave stale state interfering with the project
      // or chat panel mount.
      dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
      if (roomId && chatGroupId && !result.groupData.roomType) {
        history.push("/projects");
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false })
        dispatch({ type: SET_DEEPLINK_DATA, payload: null });
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
        dispatch({ type: SET_IN_ACTIVITY, payload: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        // Show the project right panel immediately to avoid a blank gap during the
        // 1s timeout — without this, when navigating from /todo or /messages the
        // user sees an empty area until the deferred SET_OPEN_SIDE_BAR/RIGHT_TOP fires.
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
        dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        setTimeout(() => {
          dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId, roomId, isInternal: false, page_no: result.message.page_no, messageId: result.message.id } });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        }, 1000);
      }
      else if (roomId && chatGroupId && result.groupData.roomType) {
        history.push("/projects");
        dispatch({ type: SET_DEEPLINK_DATA, payload: null });
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false })
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
        dispatch({ type: SET_IN_ACTIVITY, payload: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        // Show the project right panel immediately (see external branch above).
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
        dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        setTimeout(() => {
          dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId, roomId, isInternal: true, companyId, page_no: result.message.page_no, messageId: result.message.id } });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        }, 2000);
      }
      else if (chatGroupId) {
        history.push("/chat");
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false })
        dispatch({ type: SET_DEEPLINK_DATA, payload: null });
        dispatch({ type: TOGGLE_CHAT, payload: false })
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: SET_IN_ACTIVITY, payload: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        dispatch({ type: GROUPS, groups: [] })
        // Hide chat list panel - show only SideNav + Conversation
        dispatch({ type: SET_CHAT_FULLSCREEN, payload: true });
        // Show the chat right panel immediately to avoid a blank gap.
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'chat1' });
        dispatch({ type: RIGHT_TOP, rightTop: 'chat1' });
        setTimeout(() => {
          dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId, page_no: result.message.page_no, messageId: result.message.id } });
          dispatch({ type: FROM_FROROIEGN_PROFILE, payload: 'pageLoad' });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'chat1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'chat1' });
        }, 1000);
      }
    } catch (error) {
      console.log('error', error)
    }

  };

  const _onClickOfSearchResult = (result) => {
    if (result.sourceType === 'email') {
      navigateToEmail(buildEmailNavigatePayloadFromGlobalSearch(result));
      return;
    }
    navigateToChat(result);
  };

  // Navigate to room section and open the room (Project-Room tab)
  const navigateToRoom = (room) => {
    ensureSearchHistoryEntry();
    dismissSearchUiKeepHistory();

    const roomId = room.roomId || room.id || room.buildingId;
    const companyId = room.companyId || room.cId || (room.company && room.company.id);
    // Use truthy check for roomType (matches navigateToChat pattern: !roomType = external, roomType = internal)
    const isInternal = !!room.roomType || room.isInternal === true;

    console.log('[SearchBar] navigateToRoom - room object:', JSON.stringify(room));
    console.log('[SearchBar] navigateToRoom - roomId:', roomId, 'companyId:', companyId, 'isInternal:', isInternal, 'roomType:', room.roomType);

    dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: isInternal, loadingMessage: 'navigating_to_room' });

    try {
      // Set deeplinkData first so MyProject has it when it mounts/loads
      dispatch({ type: SET_DEEPLINK_DATA, payload: { roomId, isInternal, ...(companyId && { companyId }) } });

      history.push("/projects");
      dispatch({ type: RIGHT_TOP, rightTop: '' });
      dispatch({ type: TOGGLE_DRAWER, open: false });
      dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
      dispatch({ type: TOGGLE_PROJECT, payload: false });
      dispatch({ type: OPEN_PROJECTS, openProjects: [] });
      dispatch({ type: OPEN_CHATS, openChats: [] });
      dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
      dispatch({ type: SET_IN_ACTIVITY, payload: false });
      dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
      dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
      dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
      dispatch({ type: SET_CHAT_FULLSCREEN, payload: false });
    } catch (error) {
      console.log('Navigation error:', error);
    }
  };

  // Navigate to a group when clicked from Groups tab
  const navigateToGroup = (group) => {
    ensureSearchHistoryEntry();
    dismissSearchUiKeepHistory();

    const chatGroupId = group.id || group.groupId;
    const roomId = group.roomId;
    const companyId = group.companyId;
    const isInternal = group.roomType === true || group.roomType === 'internal';
    // A Microsoft Teams chat (no room): the room-less pane in its Teams mode via Messages.
    if (!roomId && chatGroupId && isTeamsTarget(group, chatGroupId)) {
      const known = knownTeamsChat(chatGroupId) || {};
      openChatMessage(dispatch, history, {
        chatGroupId: String(chatGroupId),
        isOneToOne: false,
        name: known.name || group.name || group.groupName || "",
        isTeams: true,
        teamsChatType: known.teamsChatType || group.teamsChatType || null,
        webUrl: known.webUrl || group.webUrl || null,
      });
      return;
    }
    
    // Pass isInternal to ensure loader shows correct color
    dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: isInternal });
    
    try {
      if (roomId && chatGroupId && !isInternal) {
        // External project room
        history.push("/projects");
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false });
        dispatch({ type: SET_DEEPLINK_DATA, payload: null });
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
        dispatch({ type: SET_IN_ACTIVITY, payload: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        setTimeout(() => {
          dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId, roomId, isInternal: false } });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        }, 1000);
      } else if (companyId && isInternal) {
        // Internal project room
        history.push("/projects");
        dispatch({ type: SET_DEEPLINK_DATA, payload: null });
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false });
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
        dispatch({ type: SET_IN_ACTIVITY, payload: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        setTimeout(() => {
          dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId, roomId, isInternal: true, companyId } });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        }, 2000);
      } else if (chatGroupId) {
        // Regular chat group
        history.push("/chat");
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false });
        dispatch({ type: SET_DEEPLINK_DATA, payload: null });
        dispatch({ type: TOGGLE_CHAT, payload: false });
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: SET_IN_ACTIVITY, payload: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        dispatch({ type: GROUPS, groups: [] });
        setTimeout(() => {
          dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId } });
          dispatch({ type: FROM_FROROIEGN_PROFILE, payload: 'pageLoad' });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'chat1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'chat1' });
        }, 1000);
      }
    } catch (error) {
      console.log('Navigation error:', error);
    }
  };

  // Navigate to user profile when clicked from Users tab
  const navigateToUser = (userItem) => {
    ensureSearchHistoryEntry();
    dismissSearchUiKeepHistory();
    if (userItem.userId === user?.id) {
      history.push('/profile');
    } else {
      history.push(`/user-profile/${userItem.userId}`);
    }
  };

  const navigateToDirectMessage = async (e, userItem) => {
    e.stopPropagation();
    if (!user?.id || !userItem?.userId || userItem.userId === user.id) return;

    try {
      const { data } = await callAPI('/group', {}, 'POST', {
        groupType: 'user',
        isOneToOne: true,
        users: [user.id, userItem.userId],
      });

      const chatData = data.chat;
      const userName = `${userItem.title ? userItem.title + ' ' : ''}${userItem.firstName || ''} ${userItem.lastName || ''}`.trim() || chatData.name;

      const activityMessageEntry = {
        id: chatData.id,
        mainId: chatData.id,
        group: {
          ...chatData,
          id: chatData.id,
          name: userName,
          isOneToOne: true,
          defaultChatGroup: Boolean(chatData.defaultChatGroup),
          isInternal: Boolean(chatData.isInternal),
        },
        senderId: userItem.userId,
        senderName: userName,
        senderAvatar: userItem.avatar || null,
        name: userName,
        avatar: userItem.avatar || null,
        isOpened: true,
        isOneToOne: true,
        unseenChatCount: 0,
        createdAt: new Date().toISOString(),
      };

      const chatPayload = {
        ...chatData,
        id: chatData.id,
        key: chatData.id,
        name: userName,
        isOpened: true,
        isOneToOne: true,
        avatar: userItem.avatar || null,
        defaultChatGroup: Boolean(chatData.defaultChatGroup),
        isInternal: Boolean(chatData.isInternal),
        onlyView: false,
        unseenChatCount: 0,
      };

      ensureSearchHistoryEntry();
      dismissSearchUiKeepHistory();

      dispatch({ type: OPEN_PROJECTS, openProjects: [] });
      dispatch({ type: GROUPS, groups: [] });
      dispatch({ type: GROUP_CHATS, groupChats: [] });

      dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
      dispatch({ type: TOGGLE_PROJECT, payload: false });
      dispatch({ type: SET_IN_ACTIVITY, status: true });
      dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
      dispatch({ type: TOGGLE_CHAT, payload: false });
      dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
      dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'LatestMessages' });
      dispatch({ type: RIGHT_TOP, rightTop: '' });

      const currentMessages = activityMessages || [];
      const updatedMessages = currentMessages
        .filter(msg => msg.group?.id !== chatData.id)
        .map(msg => ({ ...msg, isOpened: false }));
      dispatch({ type: SET_ACTIVITY_MESSAGES, messages: [activityMessageEntry, ...updatedMessages] });
      dispatch({ type: SET_ACTIVITY_MESSAGES_LAST_FETCHED, payload: Date.now() });
      dispatch({ type: OPEN_CHATS, openChats: [chatPayload] });

      // push (not replace) so Back can return to the search popup URL.
      history.push({
        pathname: '/messages',
        search: '',
        state: { fromGlobalSearch: true, searchNavId: Date.now() },
      });
    } catch (error) {
      console.error('Error navigating to direct message:', error);
    }
  };

  const navigateToEmail = (emailItem, resultKey) => {
    const emailKey = resultKey || `email-${emailItem?.id || emailItem?.messageId || emailItem?._id || emailItem?.providerMessageId || ''}`;
    saveResultsScrollPosition(emailKey);
    ensureSearchHistoryEntry();
    dismissSearchUiKeepHistory();

    // If the item already carries displayable data but wasn't built via
    // buildEmailNavigatePayloadFromGlobalSearch, promote it to a prefetched
    // email so the detail panel opens instantly instead of waiting for a GET.
    let payload = emailItem;
    if (!payload.fromGlobalSearch && (payload.subject != null || payload.body != null || payload.bodyText != null || payload.snippet != null)) {
      const names = Array.isArray(payload.attachmentNames) ? payload.attachmentNames.filter(Boolean) : [];
      const prefetchedEmail = {
        ...payload,
        hasAttachments: !!(payload.hasAttachments || names.length > 0),
        attachmentNames: names,
        attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
        __accountId: payload.__accountId || payload.emailAccountId || null,
        emailAccountId: payload.emailAccountId || payload.__accountId || null,
      };
      payload = {
        ...prefetchedEmail,
        fromGlobalSearch: true,
        prefetchedEmail,
      };
    }

    // When the user is currently on the rooms section (/projects → activeSideBar/rightTop='project1'),
    // openProjects is populated and rightTop drives RightBar to keep rendering <MyProject>.
    // The /messages route is also display:none'd in App.js while activeSideBar='project1'/'chat1'.
    // Without resetting these, clicking an email search result lands the user back on the
    // currently-open project room instead of the email view. Mirror the cleanup that
    // navigateToChat does so the email navigation works from any source view.
    dispatch({ type: RIGHT_TOP, rightTop: '' });
    dispatch({ type: SET_DEEPLINK_DATA, payload: null });
    dispatch({ type: SET_DEEPLINK_LOADING, loading: false });
    dispatch({ type: OPEN_PROJECTS, openProjects: [] });
    dispatch({ type: OPEN_CHATS, openChats: [] });
    dispatch({ type: SET_CHAT_FULLSCREEN, payload: false });
    dispatch({ type: TOGGLE_DRAWER, open: false });

    dispatch({ type: SET_PENDING_EMAIL, payload });
    dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
    dispatch({ type: TOGGLE_PROJECT, payload: false });
    dispatch({ type: SET_IN_ACTIVITY, status: true });
    dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
    dispatch({ type: TOGGLE_CHAT, payload: false });
    dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
    dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'LatestMessages' });
    // push (not replace) so the search URL (?gs=1&gq=…) stays under this entry for Back.
    history.push({
      pathname: '/messages',
      search: '',
      state: { fromGlobalSearch: true, searchNavId: Date.now() },
    });
  };

  const renderEmailSearchResult = (emailItem, index) => {
    const fromAddr = emailItem.fromAddress?.email || emailItem.fromEmail || '';
    const emailSenderName = emailItem.fromName || fromAddr || '';
    const isUnread = emailItem.isRead === false;
    const receivedDate = formatEmailListDate(emailItem.receivedAt);
    const folderLabel = getEmailFolderLabel(emailItem);
    const fullBody = getEmailSearchPreviewText(emailItem);
    const snippet = String(fullBody || emailItem.snippet || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
    const attachNames = Array.isArray(emailItem.attachmentNames)
      ? emailItem.attachmentNames.filter(Boolean)
      : [];
    const attachSnippet = String(emailItem.attachmentContentSnippet || '')
      .replace(/\s+/g, ' ')
      .trim();
    const showAttachChips = attachNames.length > 0;
    const showAttachContentSnippet = !!attachSnippet;
    const visibleAttach = attachNames.slice(0, 2);
    const extraAttach = Math.max(0, attachNames.length - visibleAttach.length);
    const { fromMatched } = getEmailAddressMatchInfo(emailItem, highlightQuery);
    const showMatchedAddr = emailFilterChip === 'addresses' && fromMatched && fromAddr;

    const resultKey = `email-${emailItem.id || index}`;
    return (
      <div
        key={resultKey}
        data-search-result-key={resultKey}
        className={`search-result-email-row${isUnread ? ' search-result-email-row--unread' : ''}`}
        onClick={() => navigateToEmail(emailItem, resultKey)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigateToEmail(emailItem, resultKey);
          }
        }}
      >
        <EmailSenderAvatar
          className="search-result-email-row-avatar email-avatar-circle"
          email={fromAddr}
          name={emailSenderName}
          size={30}
        />
        <div className="search-result-email-row-main">
          <div className="search-result-email-row-top">
            <span className={`search-result-email-row-sender${isUnread ? ' is-unread' : ''}`}>
              {highlightText(emailSenderName || fromAddr || '—', highlightQuery)}
            </span>
            <div className="search-result-email-row-meta">
              <span className="search-result-email-row-date">{receivedDate}</span>
              <span className="search-result-email-row-folder">{folderLabel}</span>
            </div>
          </div>
          {showMatchedAddr ? (
            <div className="search-result-email-row-addr">
              {highlightText(fromAddr, highlightQuery)}
            </div>
          ) : null}
          <div className={`search-result-email-row-subject${isUnread ? ' is-unread' : ''}`}>
            {emailItem.subject
              ? highlightText(emailItem.subject, highlightQuery)
              : (strings.email_no_subject || '(No subject)')}
            {(showAttachChips || showAttachContentSnippet) ? (
              <span className="search-result-email-row-paperclip" title={attachNames.join(', ')}>
                <MaterialIcon icon="attach_file" size={14} color="#888" />
              </span>
            ) : null}
          </div>
          {snippet && !(emailFilterChip === 'attachments' && showAttachContentSnippet) ? (
            <div className="search-result-email-row-snippet">
              {highlightText(snippet, highlightQuery)}
              {fullBody && fullBody.replace(/\s+/g, ' ').trim().length > 120 ? '…' : ''}
            </div>
          ) : null}
          {showAttachChips ? (
            <div className="search-result-email-row-attach-chips">
              {visibleAttach.map((name, i) => {
                const lower = String(name).toLowerCase();
                const isPdf = lower.endsWith('.pdf');
                const isImg = /\.(png|jpe?g|gif|webp|bmp)$/i.test(lower);
                return (
                  <span
                    key={`att-${emailItem.id || index}-${i}`}
                    className={`search-result-email-attach-chip${isPdf ? ' is-pdf' : ''}${isImg ? ' is-img' : ''}`}
                  >
                    <MaterialIcon
                      icon={isPdf ? 'picture_as_pdf' : isImg ? 'image' : 'attach_file'}
                      size={13}
                      color={isPdf ? '#d93025' : isImg ? '#1a73e8' : '#666'}
                    />
                    <span>{highlightText(String(name), highlightQuery)}</span>
                  </span>
                );
              })}
              {extraAttach > 0 ? (
                <span className="search-result-email-attach-chip is-more">+{extraAttach}</span>
              ) : null}
            </div>
          ) : null}
          {showAttachContentSnippet ? (
            <div className="search-result-email-row-attach-snippet">
              <MaterialIcon icon="description" size={13} color="#888" />
              <span>{highlightText(attachSnippet, highlightQuery)}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const getFilteredEmailResults = () => {
    const list = Array.isArray(emailResults) ? emailResults : [];
    switch (emailFilterChip) {
      case 'content':
        return list.filter((e) => e._matchContent);
      case 'addresses':
        return list.filter((e) => e._matchAddress);
      case 'attachments':
        return list.filter((e) => e._matchAttachment);
      case 'hasAttachments':
        return list.filter((e) => Array.isArray(e.attachmentNames) && e.attachmentNames.length > 0);
      case 'unread':
        return list.filter((e) => e.isRead === false);
      default:
        return list;
    }
  };

  // Get group display name with room prefix if available (roomName - groupName format)
  const getGroupDisplayName = (group) => {
    if (group.roomName && group.groupName) {
      return `${group.roomName} - ${group.groupName}`;
    }
    return group.roomName || group.groupName || group.name || 'Unknown Group';
  };

  // Get room initials (2 letters from room name, like project.js)
  const getRoomInitials = (group) => {
    const name = group.roomName || group.groupName || group.name || '';
    // Remove all non-alphabetic characters (keep only letters)
    const cleanedName = name.replace(/[^a-zA-Z]/g, '');
    return cleanedName.substring(0, 2).toUpperCase();
  };

  // Get group avatar initials (kept for backward compatibility)  
  const getGroupInitials = (group) => {
    const name = group.groupName || group.name || '';
    return name.substring(0, 2).toUpperCase();
  };
  
  return (
    <div className="searchbar-wrapper" ref={resultsRef}>
      <div className="searchbar-row">
        <div className="searchbar-container">
          <span className="searchbar-icon">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="7" stroke="#B0B0B0" strokeWidth="2" />
              <line x1="14.2929" y1="14.7071" x2="18" y2="18.4142" stroke="#B0B0B0" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input
            className="searchbar-input"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            onKeyDown={handleSearchKeyDown}
            placeholder={
              showEmailSearchHint
                ? (strings.search_messages_with_email || strings.search_messages)
                : (strings.search_messages)
            }
          />
        </div>
        {searchValue.trim().length > 0 && (
          isLoading && !showResults ? (
            <div className="search-loader search-loader--inline">
              <div className="loader-spinner"></div>
            </div>
          ) : (
            <button
              type="button"
              className="kj-btn kj-btn--primary kj-btn--sm searchbar-submit"
              onClick={submitSearch}
              disabled={searchValue.trim().length < 3}
            >
              {strings.search_submit || 'Search'}
            </button>
          )
        )}
      </div>
      {showResults && (
        <>
          {/* Backdrop to prevent clicks on background elements */}
          {!searchOverlaySuppressed && (
          <div 
            className="search-backdrop search-bar__backdrop"
            onClick={(e) => e.stopPropagation()}
          />
          )}
          <div
            className={`search-results${searchOverlaySuppressed ? ' search-results--suppressed' : ''}`}
            ref={resultsListRef}
            onScroll={handleScroll}
            aria-hidden={searchOverlaySuppressed ? true : undefined}
          >
          {/* Sticky header with search input and tabs */}
          <div className={`search-results-header ${isTabsHidden ? 'search-results-header--hidden' : ''}`}>
            {/* Search input inside popup */}
            <div className="search-results-input-container">
              <div className="search-results-input-row">
              <div className="search-results-input-wrapper">
                <span className="search-results-input-icon">
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="9" cy="9" r="7" stroke="#B0B0B0" strokeWidth="2" />
                    <line x1="14.2929" y1="14.7071" x2="18" y2="18.4142" stroke="#B0B0B0" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <input
                  ref={popupInputRef}
                  className="search-results-input"
                  type="text"
                  value={searchValue}
                  onChange={handleSearch}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={
                    showEmailSearchHint
                      ? (strings.search_messages_with_email || strings.search_messages)
                      : (strings.search_messages)
                  }
                  // Focus without scrolling the results list back to top (Back restore).
                  autoFocus={!shouldRestoreScrollRef.current}
                />
              </div>
                {searchValue.trim().length > 0 ? (
                  <button
                    type="button"
                    className="kj-btn kj-btn--primary search-results-submit"
                    onClick={submitSearch}
                    disabled={searchValue.trim().length < 3 || isLoading}
                  >
                    {isLoading ? (
                      <span className="loader-spinner search-results-submit-spinner" />
                    ) : (
                      strings.search_submit || 'Search'
                    )}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="search-results-popup-close"
                  onClick={closeSearchPopup}
                  aria-label={strings.close || 'Close'}
                  title={strings.close || 'Close'}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              {searchIntent.active || searchIntent.intent === 'user' ? (
                <div className="search-intent-summary" aria-live="polite">
                  <span className="search-intent-label">{strings.search_intent_understood || 'Searching'}</span>
                  {searchIntent.intent === 'email' ? (
                    <span className="search-intent-chip">{strings.search_intent_emails || 'Emails'}</span>
                  ) : null}
                  {searchIntent.intent === 'chat' ? (
                    <span className="search-intent-chip">{strings.search_intent_messages || 'Messages'}</span>
                  ) : null}
                  {searchIntent.intent === 'user' ? (
                    <span className="search-intent-chip">{strings.search_intent_user || 'User'}</span>
                  ) : null}
                  {searchIntent.person ? (
                    <span className="search-intent-chip is-person">{searchIntent.person}</span>
                  ) : null}
                  {searchIntent.active && searchIntent.content ? (
                    <span className="search-intent-chip is-content">
                      {(strings.search_email_combo_containing || 'containing')} {searchIntent.content}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            {/* Tabs — combination/user lookups stay on a single relevant tab */}
            <div className="search-results-tabs">
              {!emailComboOnly && !userLookupOnly && (searchResults.length > 0 || chatComboOnly) && (
                <span
                  className={`search-results-tab ${activeTab === 'messages' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('messages')}
                >
                  {strings.search_messages_tab || 'Messages'}
                </span>
              )}
              {!chatComboOnly && !userLookupOnly && (emailResults.length > 0 || emailComboOnly) && (
                <span
                  className={`search-results-tab ${activeTab === 'emails' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('emails')}
                >
                  {strings.search_emails_tab || 'Email'}
                  {emailResults.length > 0 ? (
                    <span className="search-results-tab-count">{emailResults.length}</span>
                  ) : null}
                </span>
              )}
              {!emailComboOnly && !chatComboOnly && !userLookupOnly && groupResults.length > 0 && (
                <span
                  className={`search-results-tab ${activeTab === 'groups' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('groups')}
                >
                  {strings.search_chat_groups || strings.search_groups || 'Chat-Groups'}
                </span>
              )}
              {!emailComboOnly && !chatComboOnly && !userLookupOnly && roomResults.length > 0 && (
                <span
                  className={`search-results-tab ${activeTab === 'rooms' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('rooms')}
                >
                  {strings.search_project_room || 'Project'}
                </span>
              )}
              {!emailComboOnly && !chatComboOnly && (userResults.length > 0 || userLookupOnly) && (
                <span
                  className={`search-results-tab ${activeTab === 'users' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('users')}
                >
                  {strings.search_users_tab || 'Users'}
                </span>
              )}
              {!emailComboOnly && !chatComboOnly && !userLookupOnly && documentResults.length > 0 && (
                <span
                  className={`search-results-tab ${activeTab === 'documents' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('documents')}
                >
                  {strings.search_documents_tab || 'Documents'}
                </span>
              )}
              {!emailComboOnly && !chatComboOnly && !userLookupOnly && todoResults.length > 0 && (
                <span
                  className={`search-results-tab ${activeTab === 'todos' ? 'search-results-tab--active' : ''}`}
                  onClick={() => selectSearchTab('todos')}
                >
                  {strings.search_todos_tab || 'ToDo'}
                </span>
              )}
            </div>
          </div>

          {/* Groups Tab Content */}
          {activeTab === 'groups' && (
            <>
              {isGroupsLoading ? (
                <div className="search-loading">
                  <div className="loader-spinner"></div>
                  <span>{strings.searching_groups || 'Searching groups...'}</span>
                </div>
              ) : groupResults.length > 0 ? (
                groupResults.map((group, index) => (
                  <div
                    key={group.id || group.groupId || index}
                    className="search-result-item search-bar__entity-row"
                    onClick={() => navigateToGroup(group)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="search-bar__entity-name">
                      {highlightText(stripMarkdown(getGroupDisplayName(group)), searchValue)}
                    </span>
                    {isTeamsChat(group) && (
                      <span className="ai-transcription-badge">
                        <span className="material-symbols-outlined search-bar__badge-icon">groups</span>
                        {strings.pro_messages_tag_teams || 'Teams'}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="22" cy="22" r="16" stroke="#E0E0E0" strokeWidth="3" fill="#fafafa" />
                      <line x1="36" y1="36" x2="46" y2="46" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
                      <circle cx="22" cy="22" r="8" stroke="var(--kjuup-accent)" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <div className="no-results-title">{strings.no_groups_found || 'No groups found'}</div>
                  <div className="no-results-desc">{strings.try_different_keyword}</div>
                </div>
              )}
            </>
          )}

          {/* Project-Room Tab Content */}
          {activeTab === 'rooms' && (
            <>
              {isRoomsLoading ? (
                <div className="search-loading">
                  <div className="loader-spinner"></div>
                  <span>{strings.searching_rooms || 'Searching projects...'}</span>
                </div>
              ) : roomResults.length > 0 ? (
                roomResults.map((room, index) => (
                  <div
                    key={room.id || room.roomId || index}
                    className="search-result-item search-bar__entity-row"
                    onClick={() => navigateToRoom(room)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="search-bar__entity-name">
                      {highlightText(room.roomName || room.name || 'Unknown Project', searchValue)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="22" cy="22" r="16" stroke="#E0E0E0" strokeWidth="3" fill="#fafafa" />
                      <line x1="36" y1="36" x2="46" y2="46" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
                      <circle cx="22" cy="22" r="8" stroke="var(--kjuup-accent)" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <div className="no-results-title">{strings.no_rooms_found || 'No projects found'}</div>
                  <div className="no-results-desc">{strings.try_different_keyword}</div>
                </div>
              )}
            </>
          )}

          {/* Users Tab Content */}
          {activeTab === 'users' && (
            <>
              {isUsersLoading ? (
                <div className="search-loading">
                  <div className="loader-spinner"></div>
                  <span>{strings.searching_users || 'Searching users...'}</span>
                </div>
              ) : userResults.length > 0 ? (
                userResults.map((userItem, index) => (
                  <div
                    key={userItem.userId || index}
                    className="search-result-item search-bar__entity-row search-bar__entity-row--user"
                    onClick={(e) => userItem.userId !== user?.id ? navigateToDirectMessage(e, userItem) : navigateToUser(userItem)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <UserAvatar
                      src={userItem.avatar ? BASE_URL + userItem.avatar : process.env.PUBLIC_URL + '/assets/img/head.svg'}
                      alt={`${userItem.firstName} ${userItem.lastName}`}
                      width={40}
                      height={40}
                      fallbackSrc={process.env.PUBLIC_URL + '/assets/img/head.svg'}
                    />
                    <span className="search-bar__entity-name search-bar__entity-name--user">
                      {userItem.title ? `${userItem.title} ` : ''}{highlightText(`${userItem.firstName || ''} ${userItem.lastName || ''}`.trim(), searchValue)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="22" cy="22" r="16" stroke="#E0E0E0" strokeWidth="3" fill="#fafafa" />
                      <line x1="36" y1="36" x2="46" y2="46" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
                      <circle cx="22" cy="22" r="8" stroke="var(--kjuup-accent)" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <div className="no-results-title">{strings.no_users_found || 'No users found'}</div>
                  <div className="no-results-desc">{strings.try_different_keyword}</div>
                </div>
              )}
            </>
          )}

          {/* Documents Tab Content */}
          {activeTab === 'documents' && (
            <>
              {documentResults.length > 0 ? (
                documentResults.map((doc, index) => {
                  const teamsDoc = isTeamsChat(doc);
                  // A Teams file opens its chat in Teams mode (it has no room).
                  const teamsFlags = teamsDoc
                    ? { source: 'teams', isTeams: true, teamsChatType: doc.teamsChatType || null }
                    : {};
                  return (
                  <div key={index} className="search-result-item search-result-professional kj-pointer"
                    onClick={() => {
                      _onClickOfSearchResult({
                        sourceType: 'chat',
                        message: { id: doc.chatId, createdAt: doc.createdAt, page_no: doc.page_no },
                        groupData: {
                          groupId: doc.groupId,
                          groupName: doc.groupName,
                          isOneToOneChat: doc.isOneToOneChat,
                          roomId: doc.roomId,
                          roomName: doc.roomName,
                          roomType: doc.roomType || 0,
                          ...teamsFlags,
                        },
                        ...teamsFlags,
                        sender: { id: doc.senderId, firstName: doc.senderFirstName, lastName: doc.senderLastName, avatar: doc.senderAvatar },
                      });
                    }}
                  >
                    <div className="result-header w-100">
                      <div className="search-bar__icon-row">
                        <span className="material-symbols-outlined search-bar__result-icon">description</span>
                        <div className="result-user-info">
                          <div className="result-group-info">
                            {teamsDoc && (
                              <div className="group-info-item search-bar__tone--accent">
                                <span className="group-label search-bar__tone--accent">
                                  {highlightText(
                                    `${strings.pro_messages_tag_teams || 'Teams'}${doc.groupName ? ` - ${doc.groupName}` : ''}`,
                                    searchValue
                                  )}
                                </span>
                              </div>
                            )}
                            {!teamsDoc && doc.roomName && (
                              <div className={`group-info-item ${doc.roomType ? 'search-bar__tone--internal' : 'search-bar__tone--accent'}`}>
                                <span className={`group-label ${doc.roomType ? 'search-bar__tone--internal' : 'search-bar__tone--accent'}`}>
                                  {highlightText(
                                    `${doc.roomName || ''}${doc.groupName ? ` - ${doc.groupName}` : ''}`,
                                    searchValue
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <span className="result-user-name">{highlightText(doc.fileName, searchValue)}</span>
                          <span className="result-date">
                            {doc.senderFirstName} {doc.senderLastName} &middot; {formatDate(doc.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="no-results">{strings.no_documents_found || 'Keine Dokumente gefunden'}</div>
              )}
            </>
          )}

          {/* Email — Outlook-style dense list with filter chips */}
          {activeTab === 'emails' && (() => {
            const filteredEmails = getFilteredEmailResults();
            const contentCnt = emailResults.filter((e) => e._matchContent).length;
            const addressCnt = emailResults.filter((e) => e._matchAddress).length;
            const attachmentCnt = emailResults.filter((e) => e._matchAttachment).length;
            const hasAttachCnt = emailResults.filter((e) => Array.isArray(e.attachmentNames) && e.attachmentNames.length > 0).length;
            const unreadCnt = emailResults.filter((e) => e.isRead === false).length;
            const chips = [
              { id: 'all', label: strings.search_email_filter_all || 'All', count: emailResults.length },
              { id: 'content', label: strings.search_email_filter_content || 'Content', count: contentCnt },
              { id: 'addresses', label: strings.search_email_filter_addresses || 'Addresses', count: addressCnt },
              { id: 'attachments', label: strings.search_email_filter_attachments || 'Attachments', count: attachmentCnt },
              { id: 'hasAttachments', label: strings.search_email_filter_has_attachments || 'Has attachments', count: hasAttachCnt },
              { id: 'unread', label: strings.search_email_filter_unread || 'Unread', count: unreadCnt },
            ].filter((c) => c.id === 'all' || c.count > 0);
            return (
              <>
                {searchIntent.intent === 'email' && searchIntent.active ? (
                  <div className="search-email-combo-hint">
                    {(strings.search_email_combo_hint || 'Searching within emails of')}{' '}
                    <strong>{searchIntent.person}</strong>
                    {searchIntent.content ? (
                      <>
                        {' · '}
                        {(strings.search_email_combo_containing || 'containing')}{' '}
                        <strong>{searchIntent.content}</strong>
                      </>
                    ) : null}
                  </div>
                ) : null}
                {!isEmailsLoading && emailResults.length > 0 ? (
                  <div className="search-email-filter-chips" role="tablist" aria-label={strings.search_emails_tab || 'Email'}>
                    {chips.map((chip) => (
                      <button
                        key={chip.id}
                        type="button"
                        role="tab"
                        aria-selected={emailFilterChip === chip.id}
                        className={`search-email-filter-chip${emailFilterChip === chip.id ? ' is-active' : ''}`}
                        onClick={() => setEmailFilterChip(chip.id)}
                      >
                        {chip.label}
                        <span className="search-email-filter-chip-count">{chip.count}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                {isEmailsLoading ? (
                  <div className="search-loading">
                    <div className="loader-spinner"></div>
                    <span>{strings.searching_emails || 'Searching emails...'}</span>
                  </div>
                ) : filteredEmails.length > 0 ? (
                  <div className="search-email-results-list">
                    {filteredEmails.map((emailItem, index) =>
                      renderEmailSearchResult(emailItem, index)
                    )}
                  </div>
                ) : (
                  <div className="no-results">
                    <div className="no-results-icon">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="22" cy="22" r="16" stroke="#E0E0E0" strokeWidth="3" fill="#fafafa" />
                        <line x1="36" y1="36" x2="46" y2="46" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
                        <circle cx="22" cy="22" r="8" stroke="var(--kjuup-accent)" strokeWidth="2" fill="none" />
                      </svg>
                    </div>
                    <div className="no-results-title">{strings.no_emails_found || 'No emails found'}</div>
                    <div className="no-results-desc">{strings.try_different_keyword}</div>
                  </div>
                )}
              </>
            );
          })()}

          {/* ToDo Tab Content */}
          {activeTab === 'todos' && (
            <>
              {todoResults.length > 0 ? (
                todoResults.map((todoItem, index) => (
                  <div
                    key={todoItem.todoId || index}
                    className="search-result-item search-result-professional kj-pointer"
                    onClick={() => {
                      ensureSearchHistoryEntry();
                      dismissSearchUiKeepHistory();
                      // The task opens inside its project, on Aufgaben (no project: GET /todo/:id).
                      openProjectTask(dispatch, history, {
                        todoId: todoItem.todoId || todoItem.id,
                        roomId: todoItem.roomId || todoItem.buildingList || null,
                        roomName: todoItem.roomName || "",
                      });
                    }}
                  >
                    <div className="result-header w-100">
                      <div className="search-bar__icon-row">
                        <span className="material-symbols-outlined search-bar__result-icon">list_alt_check</span>
                        <div className="result-user-info">
                          <div className="result-group-info">
                            {(todoItem.roomName || todoItem.groupName) && (
                              <div className="group-info-item search-bar__tone--accent">
                                <span className="group-label search-bar__tone--accent">
                                  {highlightText(
                                    `${todoItem.roomName || ''}${todoItem.roomName && todoItem.groupName ? ` - ${todoItem.groupName}` : (todoItem.groupName || '')}`,
                                    searchValue
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <span className="result-user-name">{highlightText(todoItem.description || '', searchValue)}</span>
                          <span className="result-date">
                            {todoItem.createdByFirstName} {todoItem.createdByLastName} &middot; {formatDate(todoItem.createdAt)}
                            {todoItem.status && <span className={`search-bar__todo-status${todoItem.status === 'completed' ? ' search-bar__todo-status--completed' : ''}`}>{todoItem.status}</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="22" cy="22" r="16" stroke="#E0E0E0" strokeWidth="3" fill="#fafafa" />
                      <line x1="36" y1="36" x2="46" y2="46" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
                      <circle cx="22" cy="22" r="8" stroke="var(--kjuup-accent)" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  <div className="no-results-title">{strings.no_todos_found || 'No ToDos found'}</div>
                  <div className="no-results-desc">{strings.try_different_keyword}</div>
                </div>
              )}
            </>
          )}

          {/* Messages Tab Content */}
          {activeTab === 'messages' && (
            <>
              {isLoading && page === 1 ? (
                <div className="search-loading">
                  <div className="loader-spinner"></div>
                  <span>{strings.searching_messages}</span>
                </div>
              ) : searchResults.length > 0 ? (
            searchResults.map((result, index) => {
              const isEmailResult = result.sourceType === 'email';
              const emailFromAddr = isEmailResult ? globalSearchEmailFromAddress(result) : '';
              const emailSubject = result.message?.subject || '';
              const emailToCsv = isEmailResult ? globalSearchEmailToAddressesCsv(result) : '';
              const isCompleted = result.groupData?.isCompleted === true;
              const isLeave = result.groupData?.isLeave === true;
              const isJoin = result.groupData?.isJoin;
              const isOneToOneChat = result.groupData?.isOneToOneChat;
              const teamsHit = !isEmailResult && isTeamsChat(result);
              // Hide reply option when: (isLeave OR isCompleted) AND it's a group chat (isOneToOneChat === 0); email hits are not chat replies.
              // A Teams hit is answered in its chat (the inline reply sends to the native chat endpoint).
              const canReply =
                !isEmailResult &&
                !teamsHit &&
                !(isOneToOneChat === 0 && (isLeave || isCompleted)) &&
                (isJoin !== false || (isOneToOneChat != null && isOneToOneChat !== 0));
              const chatSenderName = [result.sender?.firstName, result.sender?.lastName]
                .filter((p) => p != null && String(p).trim() !== '')
                .join(' ') || (teamsHit ? (result.sender?.name || strings.teams_user_fallback || 'Teams user') : '');
              return (
              <div
                key={index}
                className="search-result-item search-result-professional kj-pointer"
                onClick={() => _onClickOfSearchResult(result)}
              >
                <div
                  className={`result-header position-relative${isEmailResult ? ' search-bar__result-header--email' : ''}`}
                >
                  {isEmailResult ? (
                    <EmailSenderAvatar
                      className="result-avatar email-avatar-circle search-bar__email-avatar"
                      email={emailFromAddr}
                      name={globalSearchEmailFromName(result)}
                      size={30}
                    />
                  ) : (
                    <UserAvatar
                      className="result-avatar"
                      src={result.sender?.avatar ? BASE_URL + result.sender.avatar : process.env.PUBLIC_URL + '/assets/img/profile.png'}
                      alt="User avatar"
                      fallbackSrc={process.env.PUBLIC_URL + '/assets/img/profile.png'}
                    />
                  )}
                  <div
                    className={`result-user-info${isEmailResult ? ' search-bar__user-info--email' : ''}`}
                  >
                    {isEmailResult ? (
                      <>
                        {(emailFromAddr || emailToCsv) && (
                          <div className="result-group-info search-bar__email-addresses">
                            {emailFromAddr ? (
                              <div className="group-info-item search-bar__email-meta">
                                <span className="group-label search-bar__email-meta-label">
                                  {strings.email_from || 'From'}: {highlightText(emailFromAddr, searchValue)}
                                </span>
                              </div>
                            ) : null}
                            {emailToCsv ? (
                              <div className="group-info-item search-bar__email-meta">
                                <span className="group-label search-bar__email-meta-label">
                                  {strings.email_to || 'To'}: {highlightText(emailToCsv, searchValue)}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        )}
                        <div className="search-bar__email-name-row">
                          <span className="result-user-name">
                            {highlightText(globalSearchEmailFromName(result), searchValue)}
                          </span>
                          <span className="result-date">
                            {formatDate(result.message?.receivedAt || result.message?.createdAt)}
                          </span>
                        </div>
                        {emailSubject ? (
                          <div className="group-info-item search-bar__email-meta search-bar__email-meta--full">
                            <span className="group-label search-bar__email-subject-label">
                              {highlightText(emailSubject, searchValue)}
                            </span>
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <span className="result-user-name">
                          {result?.groupData?.isOneToOneChat
                            ? highlightText(result.groupData.groupName || '', searchValue)
                            : result.sender.id == user.id
                              ? 'You'
                              : highlightText(chatSenderName, searchValue)}
                        </span>
                        {!result.groupData?.isOneToOneChat && (
                          <div className="result-group-info">
                            {result.groupData && (
                              <div className={`group-info-item ${result.groupData.roomType ? 'search-bar__tone--internal' : 'search-bar__tone--accent'}`}>
                                <span className={`group-label ${result.groupData.roomType ? 'search-bar__tone--internal' : 'search-bar__tone--accent'}`}>
                                  {highlightText((result.groupData.roomName || '') + (result.groupData.roomName ? ' - ' : ''), searchValue)}
                                </span>
                                <span className={`group-label ${result.groupData.roomType ? 'search-bar__tone--internal' : 'search-bar__tone--accent'}`}>
                                  {result.groupData.defaultChatGroup
                                    ? `${isGerm ? 'Alle' : 'All'}`
                                    : highlightText(stripMarkdown(result.groupData.groupName || ''), searchValue)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        <span className="result-date">
                          {formatDate(result.message?.createdAt)}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Reply button - top right aligned; hide when completed or when not joined and not one-to-one */}
                  <div className="search-bar__reply-toggle">
                    {canReply && replyingTo !== index && (
                      <button
                        className="kj-btn kj-btn--icon kj-btn--neutral"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchGroupSecret(result.groupData.groupId);
                          fetchGroupMembers(result.groupData.groupId);
                          setReplyExiting(null);
                          setReplyText('');
                          clearAttachments();
                          setTimeout(() => {
                            setReplyingTo(index);
                          }, 50);
                        }}
                        title={strings.reply}
                      >
                        <span className="material-symbols-outlined">reply</span>
                      </button>
                    )}
                    {canReply && replyingTo === index && (
                      <button
                        className="kj-btn kj-btn--icon kj-btn--neutral"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeReplySection();
                        }}
                        title={strings.cancel_reply}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div className="result-message-content">
                  {(() => {
                    const hasTranscription = result.message?.transcribedAudio != null && result.message.transcribedAudio !== '';
                    const hasImageDescription = result.message?.imageDescription != null && result.message.imageDescription !== '' && searchValue && result.message.imageDescription.toLowerCase().includes(searchValue.toLowerCase());
                    // Used only to surface the matched document's filename (no UI for the OCR text itself).
                    const docSummaryRaw = result.message?.docFileSummary || '';
                    const docSummaryTranslated = result.message?.translatedDocFileSummary || '';
                    const hasDocSummaryMatch = (() => {
                      if (!searchValue) return false;
                      if (!docSummaryRaw && !docSummaryTranslated) return false;
                      const words = searchValue.trim().toLowerCase().split(/\s+/).filter(Boolean);
                      const lRaw = docSummaryRaw.toLowerCase();
                      const lTrans = docSummaryTranslated.toLowerCase();
                      return words.some(w => lRaw.includes(w) || lTrans.includes(w));
                    })();
                    const isOwnMessage = result.sender?.id === user?.id;
                    const hasTranslatedMessage = isAutoTranslation && !isOwnMessage && result.message?.translatedMessage;
                    const hasTranslatedAudio = isAutoTranslation && !isOwnMessage && result.message?.translatedAudio;
                    const rawText = hasTranscription
                      ? (hasTranslatedAudio ? result.message.translatedAudio : result.message.transcribedAudio)
                      : isEmailResult
                        ? getEmailSearchPreviewText(result.message)
                        : (hasTranslatedMessage ? result.message.translatedMessage : (result.message?.message || result.message?.attachedFileName || ''));
                    // Email bodies are not chat mentions; onlyUsername can mangle "<email>" / markdown-like text.
                    const fullMessage = isEmailResult ? String(rawText || '') : onlyUsername(rawText, strings);
                    const messageId = result.message?.id || index;
                    const isExpanded = expandedMessages[messageId];
                    const emailPreviewMax = 400;
                    const truncateAt = isEmailResult ? emailPreviewMax : MAX_MESSAGE_LENGTH;
                    const needsTruncation = fullMessage.length > truncateAt;
                    const displayMessage = (needsTruncation && !isExpanded)
                      ? fullMessage.substring(0, truncateAt)
                      : fullMessage;
                    const sourceType = result.sourceType === 'email' ? 'email' : 'chat';
                    
                    return (
                      <>
                        {(sourceType === 'email' || sourceType === 'chat' || hasTranscription || hasImageDescription) && (
                          <div className="search-bar__badges">
                            {sourceType === 'email' && (
                              <span className="ai-transcription-badge">
                                <span className="material-symbols-outlined search-bar__badge-icon">mail</span>
                                {strings.email || 'Email'}
                              </span>
                            )}
                            {sourceType === 'chat' && (
                              <span className="ai-transcription-badge">
                                <span className="material-symbols-outlined search-bar__badge-icon">{teamsHit ? 'groups' : 'chat'}</span>
                                {teamsHit
                                  ? (strings.pro_messages_tag_teams || 'Teams')
                                  : (strings.search_result_kjuup_chat || 'Messages Kjuup Chat')}
                              </span>
                            )}
                            {hasTranscription && (
                              <span className="ai-transcription-badge">
                                <span className="material-symbols-outlined search-bar__badge-icon">hotel_class</span>
                                {strings.ki_transcript_voice_message || 'KI-Transcript of Voice Message'}
                              </span>
                            )}
                            {hasImageDescription && (
                              <span className="ai-transcription-badge">
                                <span className="material-symbols-outlined search-bar__badge-icon">image_search</span>
                                {strings.ki_image_analysis || 'AI-Powered Image Analysis'}
                              </span>
                            )}
                            {(hasTranslatedMessage || hasTranslatedAudio) && (
                              <span className="ai-transcription-badge">
                                <span className="material-symbols-outlined search-bar__badge-icon">translate</span>
                                {isGerm ? 'Übersetzt' : 'Translated'}
                              </span>
                            )}
                          </div>
                        )}
                        {result.message?.messageType === 'image' && result.message?.fileUrl && (() => {
                          const fileUrls = Array.isArray(result.message.fileUrl) 
                            ? result.message.fileUrl 
                            : [result.message.fileUrl];
                          const attachedFileNames = Array.isArray(result.message.attachedFileName)
                            ? result.message.attachedFileName
                            : [result.message.attachedFileName];
                          return (
                            <div className={`search-bar__thumbs${displayMessage && displayMessage.trim() !== '' ? ' search-bar__thumbs--spaced' : ''}`}>
                              {fileUrls.slice(0, 4).map((url, imgIdx) => {
                                const imgUrl = buildImageUrl(false, encodeURI(url), attachedFileNames[imgIdx]);
                                if (!imgUrl) return null;
                                return (
                                  <img
                                    key={imgIdx}
                                    src={imgUrl}
                                    alt=""
                                    className="search-bar__thumb"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                );
                              })}
                              {fileUrls.length > 4 && (
                                <div className="search-bar__thumb-more">
                                  +{fileUrls.length - 4}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {!(result.message?.messageType === 'image' && result.message?.fileUrl
                          && (displayMessage.trim().toLowerCase() === (strings?.s_image || 'image').toLowerCase()
                            || /^image$/i.test(displayMessage.trim())))
                          && (
                            <div
                              className="result-message search-bar__message"
                            >
                              {highlightText(displayMessage, searchValue)}
                            </div>
                          )}
                        {hasDocSummaryMatch && result.message?.attachedFileName && (
                          <div className="search-bar__doc-match">
                            <span className="material-symbols-outlined search-bar__doc-match-icon">description</span>
                            <span className="search-bar__doc-match-name">
                              {highlightText(
                                Array.isArray(result.message.attachedFileName)
                                  ? result.message.attachedFileName.join(', ')
                                  : result.message.attachedFileName,
                                searchValue
                              )}
                            </span>
                          </div>
                        )}
                        {needsTruncation && !isExpanded && (
                          <span
                            className="search-result-more-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMessageExpansion(messageId);
                            }}
                          >
                            ...{isGerm ? 'mehr' : 'more'}
                          </span>
                        )}
                        {needsTruncation && isExpanded && (
                          <span
                            className="search-result-less-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMessageExpansion(messageId);
                            }}
                          >
                            ...{isGerm ? 'weniger' : 'less'}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                {canReply && replyingTo === index && (
                  <div
                    className={`reply-box search-bar__reply-box ${replyExiting === index ? 'reply-exit' : ''}`} key={`reply-${index}`}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}>
                    <div 
                      className={`reply-input-wrapper${(selectedDoc || recordedAudio || isRecording) ? ' d-none' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="chat-wysiwyg-toolbar search-bar__reply-toolbar">
                        <button type="button" className={`chat-wysiwyg-btn${activeFormats.bold ? ' active' : ''}`} title="Bold" onMouseDown={(e) => { e.preventDefault(); applyComposerFormat("bold"); setTimeout(updateActiveFormats, 0); }}><MaterialIcon icon="format_bold" size={18} /></button>
                        <button type="button" className={`chat-wysiwyg-btn${activeFormats.italic ? ' active' : ''}`} title="Italic" onMouseDown={(e) => { e.preventDefault(); applyComposerFormat("italic"); setTimeout(updateActiveFormats, 0); }}><MaterialIcon icon="format_italic" size={18} /></button>
                        <button type="button" className={`chat-wysiwyg-btn${activeFormats.underline ? ' active' : ''}`} title="Underline" onMouseDown={(e) => { e.preventDefault(); applyComposerFormat("underline"); setTimeout(updateActiveFormats, 0); }}><MaterialIcon icon="format_underlined" size={18} /></button>
                        <span className="chat-wysiwyg-separator" />
                        <button type="button" className={`chat-wysiwyg-btn${activeFormats.unordered ? ' active' : ''}`} title="Bullet list" onMouseDown={(e) => { e.preventDefault(); applyComposerFormat("unordered"); setTimeout(updateActiveFormats, 0); }}><MaterialIcon icon="format_list_bulleted" size={18} /></button>
                        <button type="button" className={`chat-wysiwyg-btn${activeFormats.ordered ? ' active' : ''}`} title="Numbered list" onMouseDown={(e) => { e.preventDefault(); applyComposerFormat("ordered"); setTimeout(updateActiveFormats, 0); }}><MaterialIcon icon="format_list_numbered" size={18} /></button>
                      </div>
                      <div
                        ref={richEditorRef}
                        className="chat-rich-editor search-bar__reply-editor"
                        contentEditable
                        suppressContentEditableWarning
                        data-placeholder={strings.type_your_reply || "Type your reply..."}
                        onPaste={pasteAsPlainText}
                        onDrop={handleComposerDrop}
                        onInput={handleRichEditorInput}
                        onKeyDown={(e) => {
                          const outcome = handleComposerEnterKey(e, {
                            editor: richEditorRef.current,
                          });
                          if (outcome === "send") {
                            if (!replyLoading && !isRecording && (replyText.trim() || hasAttachment())) {
                              replyToMessage(result);
                            }
                          }
                        }}
                      />
                      {showMentionSuggestions && mentionSuggestions.length > 0 && (
                        <div className="search-bar__mentions">
                          {mentionSuggestions
                            .filter((mention) =>
                              mention.name?.toLowerCase().includes(mentionInput?.toLowerCase())
                            )
                            .map((mention) => (
                              <div
                                key={mention.id}
                                className="search-bar__mention-row"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => onMentionSelect(mention)}
                              >
                                <div>
                                  {mention.type === 'All' ? (
                                    <span
                                      className="material-symbols-outlined search-bar__mention-all-icon"
                                    >group</span>
                                  ) : (
                                    <img
                                      src={mention.avatar ? BASE_URL + mention.avatar : process.env.PUBLIC_URL + '/assets/img/profile.png'}
                                      className="search-bar__mention-avatar"
                                      alt=""
                                      onError={(e) => { e.target.src = process.env.PUBLIC_URL + '/assets/img/profile.png'; }}
                                    />
                                  )}
                                </div>
                                <div className="search-bar__mention-name">
                                  {mention.name}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    {attachmentError && (
                      <div className="attachment-error">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 8V12" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 16H12.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>{attachmentError}</span>
                      </div>
                    )}
                    {selectedDoc && (
                      <div className="selected-doc-preview">
                        <div className="doc-preview-content">
                          <span className="doc-icon">{getFileIcon(selectedDoc.name?.split('.').pop())}</span>
                          <span className="doc-name">{selectedDoc.name}</span>
                          <button
                            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                            onClick={() => setSelectedDoc(null)}
                            title={strings.remove_document}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedImage && (
                      <div className="selected-image-preview">
                        <div className="image-preview-content">
                          <img src={selectedImage.preview} alt="Selected" className="preview-image" />
                          <button
                            className="remove-image-btn"
                            onClick={() => setSelectedImage(null)}
                            title={strings.remove_image}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedImages.length > 0 && (
                      <div className="selected-images-preview">
                        <div className="images-preview-header">
                          <span className="images-count">{selectedImages.length} {selectedImages.length === 1 ? (strings.image) : (strings.images)}</span>
                          <button
                            className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--danger"
                            onClick={() => setSelectedImages([])}
                            title={strings.remove_all_images}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                        <div className="images-grid">
                          {selectedImages.map((img, index) => (
                            <div key={index} className="image-preview-item">
                              <img src={img.preview} alt={`Selected ${index + 1}`} className="preview-image" />
                              <button
                                className="remove-single-image-btn"
                                onClick={() => {
                                  const newImages = selectedImages.filter((_, i) => i !== index);
                                  if (newImages.length === 1) {
                                    setSelectedImage(newImages[0]);
                                    setSelectedImages([]);
                                  } else {
                                    setSelectedImages(newImages);
                                  }
                                }}
                                title={strings.remove_image}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M18 6L6 18M6 6L18 18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {recordedAudio && (
                      <div className="selected-audio-preview search-bar__audio-preview">
                        <audio
                          controls
                          ref={audioPreviewRef}
                          src={recordedAudio.url}
                          className="search-bar__audio-player"
                        />
                        <button
                          className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger search-bar__audio-remove"
                          onClick={removeAudio}
                          title={strings.remove_recording}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <div className="reply-actions">
                      <div className="reply-attachments">
                        <label
                          className={`attachment-btn mb-0 ${hasAttachment() && !selectedImage && selectedImages.length === 0 ? 'disabled' : ''}`}
                          title={hasAttachment() && !selectedImage && selectedImages.length === 0 ? (isGerm ? DE.please_remove_current_attachment + " " + (strings.upload_image) : EN.please_remove_current_attachment + " " + EN.upload_image) : (strings.upload_image)}
                        >
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.heic,.HEIC"
                            multiple
                            onChange={async (e) => {
                              if (hasAttachment() && !selectedImage && selectedImages.length === 0) {
                                showAttachmentError(isGerm ? DE.please_remove_current_attachment + " " + (strings.upload_image) : EN.please_remove_current_attachment + " " + EN.upload_image);
                                e.target.value = '';
                                return;
                              }
                              const files = Array.from(e.target.files);
                              if (files.length === 0) return;

                              const validFiles = [];
                              for (const file of files) {
                                const fileType = file.name?.split(".").pop();
                                const allowTypes = ["jpg", "png", "jpeg", "JPG", "PNG", "JPEG", 'heic', 'HEIC'];
                                const validImage = allowTypes.filter((val) => val === fileType?.toLowerCase());
                                if (validImage.length > 0) {
                                  const preview = await getImagePreview(file);
                                  validFiles.push({ file, preview });
                                } else {
                                  showAttachmentError(strings.please_upload_image_file);
                                }
                              }

                              if (validFiles.length > 0) {
                                if (validFiles.length === 1) {
                                  setSelectedImage(validFiles[0]);
                                } else {
                                  setSelectedImages(validFiles);
                                }
                              }
                              e.target.value = '';
                            }}
                            className="d-none"
                            disabled={hasAttachment() && !selectedImage && selectedImages.length === 0}
                          />
                          <span className="material-symbols-outlined">perm_media</span>
                        </label>
                        <label
                          className={`attachment-btn mb-0 ${hasAttachment() && !selectedDoc ? 'disabled' : ''}`}
                          title={hasAttachment() && !selectedDoc ? (isGerm ? DE.please_remove_current_attachment + " " + (strings.upload_document) : EN.please_remove_current_attachment + " " + EN.upload_document) : (strings.upload_document)}
                        >
                          <input
                            type="file"
                            accept={DOC_ACCEPT_ATTRIBUTE}
                            onChange={async (e) => {
                              if (hasAttachment() && !selectedDoc) {
                                showAttachmentError(isGerm ? DE.please_remove_current_attachment + " " + (strings.upload_document) : EN.please_remove_current_attachment + " " + EN.upload_document);
                                e.target.value = '';
                                return;
                              }
                              const file = e.target.files[0];
                              if (!file) return;
                              const fileType = file.name?.split(".").pop();
                              if (DOC_EXTENSIONS.includes(fileType?.toLowerCase())) {
                                setSelectedDoc(file);
                              } else {
                                showAttachmentError(strings.please_upload_valid_document);
                              }
                              e.target.value = '';
                            }}
                            className="d-none"
                            disabled={hasAttachment() && !selectedDoc}
                          />
                          <span className="material-symbols-outlined">description</span>
                        </label>
                        <button
                          className={`kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral attachment-btn ${hasAttachment() && !isRecording && !recordedAudio ? 'disabled' : ''} ${isRecording ? 'recording' : ''}`}
                          title={hasAttachment() && !isRecording && !recordedAudio ? (isGerm ? DE.please_remove_current_attachment + " " + (strings.record_audio) : EN.please_remove_current_attachment + " " + EN.record_audio) : (isRecording ? (strings.stop_recording) : (strings.record_audio))}
                          onClick={isRecording ? stopRecording : startRecording}
                          disabled={hasAttachment() && !isRecording && !recordedAudio}
                        >
                          <span className="material-symbols-outlined">mic</span>
                          {isRecording && (
                            <div className="recording-indicator">
                              <div className="recording-dot"></div>
                              <span className="recording-time">{Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}</span>
                            </div>
                          )}
                        </button>
                      </div>
                      <button className="kj-btn kj-btn--outline" onClick={() => replyToMessage(result)} disabled={replyLoading || isRecording || (!replyText.trim() && !hasAttachment())}>
                        {replyLoading ? <div className="loader-spinner search-bar__send-spinner"></div> : (strings.send)}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
            })
          ) : (
            <div className="no-results">
              <div className="no-results-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="22" cy="22" r="16" stroke="#E0E0E0" strokeWidth="3" fill="#fafafa" />
                  <line x1="36" y1="36" x2="46" y2="46" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="22" cy="22" r="8" stroke="var(--kjuup-accent)" strokeWidth="2" fill="none" />
                </svg>
              </div>
              <div className="no-results-title">{strings.no_messages_found}</div>
              <div className="no-results-desc">{strings.try_different_keyword}</div>
            </div>
          )}
          {isLoading && page > 1 && (
            <div className="search-loading">
              <div className="loader-spinner"></div>
              <span>{strings.loading_more}</span>
            </div>
          )}
            </>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default SearchBar; 